import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ApiService } from 'src/app/api/api.service';
import * as dayjs from 'dayjs';
import { AlignStepDueDateToTask, CalculateRelativeTaskDates, TaskDueDateLabel, TaskStartDateLabel, UsesProjectDates } from '../../services/task-project-dates.util';
import { TASK_MEMBER_PLACEHOLDER, TaskMemberOptionsCache } from '../../services/task-assignment.util';
import { AlertController, ModalController } from '@ionic/angular';
import { CreatePage as CreateTaskPage } from 'src/app/tasks/create/create.page';
import { GlobalService } from 'src/app/api/global.service';
import { CompaniesContactsSelectPage as ContactsSelectPage } from 'src/app/modals/companies-contacts-select/companies-contacts-select.page';
import { SelectPage as CompaniesSelectPage } from 'src/app/companies/select/select.page';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { ManagerComponent as GroupsManagerPage } from 'src/app/components/groups/manager/manager.component';
import { SelectPage as TeamMemberSelectPage } from 'src/app/team-members/select/select.page';
import { ClientStepTypes, StepTypes } from 'src/app/components/steps/StepTypes';
import { ProjectsService } from 'src/app/api/projects.service';
import type { RepeatRules } from 'src/app/api/projects.service';
import { AddressBookComponent } from 'src/app/components/address-book/address-book.component';
import { TemplateBookComponent } from 'src/app/components/projects/template-book/template-book.component';
import { UpdateRelatedProjectsComponent } from 'src/app/components/projects/update-related-projects/update-related-projects.component';
import { ClientSchedulePreviewComponent } from 'src/app/components/projects/client-schedule-preview/client-schedule-preview.component';
import { ManageSkillsComponent } from 'src/app/components/team-members/manage-skills/manage-skills.component';
import { TaskDatesComponent } from 'src/app/components/tasks/task-dates/task-dates.component';
import { ScheduleChangedProjectsComponent } from 'src/app/components/projects/schedule-changed-projects/schedule-changed-projects.component';


export interface Task {
  id?: string;
  title?: string;
  steps?: any;
}


@Component({
  selector: 'app-create',
  templateUrl: './create.page.html',
  styleUrls: ['./create.page.scss'],
})


export class CreatePage implements OnInit {
  UsesProjectDates = UsesProjectDates;
  TaskStartDateLabel = TaskStartDateLabel;
  TaskDueDateLabel = TaskDueDateLabel;
  TASK_MEMBER_PLACEHOLDER = TASK_MEMBER_PLACEHOLDER;
  private task_member_options_cache = new TaskMemberOptionsCache();
  stepTypeEnum = StepTypes;

  show_paging = true;
  max_per_page = 25;
  today: any;
  team_members_valid = true;
  checking_permissions = false;
  items: MenuItem[];
  template_tasks: any;
  project_tasks: any;
  project_templates: any;
  task_templates: any;
  selected_project_template: any;
  selected_task_template: any;
  selected_billing_group: any;
  billing_groups: any;
  projectInternalStatuses: any[] = [];
  loaded_internal_statuses = false;
  activeIndex: number = 0;
  activeIndexString = 'Get Started';

  readonly assignmentSettingsOptions = [
    { value: 3, label: 'Tasks Auto Assigned - Auto-Assign Based on Skills & Availability', description: 'Fusion Workflow will automatically assign one of the specified team members that is assigned to this project to each task based on their skills and schedule availability once the project starts.  However, team members can easily reassign tasks based on their work load as the project progresses as well.' },
    { value: 2, label: 'Tasks Assigned - Specify Team Members', description: 'You specifically assign which team members will work on each task as part of the initial project setup.  However, team members can easily reassign tasks based on availability and work load as the project progresses as well.' },
    { value: 1, label: 'Tasks Unassigned - Team Members Can Claim', description: 'All the Tasks will initially be unassigned when the projects of this type are created and any of the specified team members assigned to this project will be able to assign themselves to each task once the project starts.  However, team members can easily reassign tasks based on availability and work load as the project progresses as well.' },
    { value: 0, label: 'Tasks Unassigned - Only Project Leader Can Assign', description: 'All the Tasks will initially be unassigned when the projects of this type are created and only the project leader will be able to assign team members to each task once the project starts.' }
  ];
  myjson: any = JSON;
  dayjs: any = dayjs;
  created_with_ai = false;
  save_as_template_ai_override = false;
  client_access_error = false;
  client_access_error_message = '';
  // The contacts / companies the client access warning is about - shown as the same badges as the Clients & Companies card
  client_access_inactive_clients: any[] = [];
  client_access_inactive_companies: any[] = [];
  skills_warning = false;
  skills_warning_message = '';
  checking_task_skills = false;
  skills_warning_ignored = false;

  companies: any;
  clients: any;
  // guid -> full client, rebuilt whenever this.clients loads so lookups don't scan the whole list
  private clients_by_guid = new Map<string, any>();
  combined_assignment_data: any[] = [];
  team_members: any = []; // Available team members for dropdown selection
  clients_roles: any[] = [];
  changed_projects: any[] = []; // Projects that have been changed and won't be updated
  related_schedules: any[] = [];
  assignment_search_query = '';
  assignment_suggestions: any[] = [];
  assignment_suggestions_active_index = -1;
  show_assignment_suggestions = false;
  assignment_search_blurred_with_text = false;

  // Typeahead for Groups/Tags - same pattern as the client/company assignment search above, just
  // filtering a flat title match instead. The existing "+" button modals (presentSelectGroupsModal/
  // presentSelectTagsModal) are left in place for bulk selection or creating a brand new one.
  all_groups: any[] = [];
  group_search_query = '';
  group_suggestions: any[] = [];
  group_suggestions_active_index = -1;
  show_group_suggestions = false;

  all_tags: any[] = [];
  tag_search_query = '';
  tag_suggestions: any[] = [];
  tag_suggestions_active_index = -1;
  show_tag_suggestions = false;

  @Input() edit_template_mode = false;
  @Input() edit_schedule_mode = false;
  @Input() edit_all_related = false;
  back_date = '';
  @Input() grouped_schedules_info: any[] = [];
  // Used by the Client Track import wizard (manage-firm.page.ts's CtdOpenProjectCreate) to jump
  // straight to a pre-picked template with its matched clients/companies already assigned, instead
  // of making the user pick a template and assignments manually via CreateFromKeyfile/LoadFromTemplate.
  @Input() preselected_template: any = null;
  @Input() preselected_clients: any[] = [];
  @Input() preselected_companies: any[] = [];
  @Input() project: any = {
    title: "",
    description: "",
    row_colour: "#FFFFFF",
    status: "0",
    assign_to_person: false,
    force_order: false,
    tasks: [],
    internal_start_date: "2023-01-07",
    internal_end_date: "2023-01-20",
    external_start_date: "2023-01-07",
    external_end_date: "2023-01-15",
    create_template: true,
    template_name: '',
    template_description: '',
    CTID: '',
    add_numbers: false,
    tags: [],
    groups: [],
    assigned_companies: [],
    assigned_clients: [],
    team_members: [],
    repeat: true,
    is_internal: false,
    repeat_rules: {
      version: "1",
      // Basic settings
      frequency: 'monthly', // 'daily', 'weekly', 'monthly', 'yearly', 'custom'
      interval: 1,
      pattern: 'monthly', // Legacy field - kept for backward compatibility

      // Weekly settings
      weekdays: [false, false, false, false, false, false, false], // Sun, Mon, Tue, Wed, Thu, Fri, Sat

      // Monthly settings
      monthly_type: 'special', // 'day', 'weekday', 'special'
      months: [true, true, true, true, true, true, true, true, true, true, true, true],
      monthly_any: true,
      monthly_day: 1, // Day of month (1-31)
      monthly_ordinal: '1', // 'first', 'second', 'third', 'fourth', 'last'
      monthly_weekday: '1', // Day of week for ordinal selection
      monthly_special: 'start', // Special monthly options

      // Yearly settings
      yearly_month: 1, // Month (1-12)
      yearly_day: 1, // Day of month (1-31)
      yearly_use_ordinal: false, // Use ordinal selection instead of fixed day
      yearly_ordinal: '1', // 'first', 'second', 'third', 'fourth', 'last'
      yearly_weekday: '1', // Day of week for ordinal selection
      yearly_ordinal_month: 1, // Month for ordinal selection (1-12)

      // Custom/Advanced settings
      custom: '', // JSON or CRON-like syntax for complex schedules

      // End repeat settings
      end_type: 'never', // 'never', 'date', 'after'
      end_date: null, // End date when end_type is 'date'
      end_after: 1, // Number of occurrences when end_type is 'after'

      // Semi-monthly settings
      semi_monthly_first_day: 1,
      semi_monthly_second_day: 15,
      repeat_start_date: '2026-01-01',
      // Default: every date falls in the repeat date's own month - start dates on the start of the month,
      // end dates on the end of it ("Start/End of Month" needs the offset unit to be Month(s)).
      internal_end_offset_unit: 'month',
      internal_start_offset_unit: 'month',
      external_start_offset_unit: 'month',
      external_end_offset_unit: 'month',
      period_start_offset_unit: 'month',
      period_end_offset_unit: 'month',
      period_start_offset: 0,
      period_end_offset: 0,
      relative_date_type: "project_repeat_date",
      internal_start_offset: 0,
      internal_end_offset: 0,
      external_start_offset: 0,
      external_end_offset: 0,
      internal_start_offset_type: 'start_of_month',
      internal_end_offset_type: 'end_of_month',
      external_start_offset_type: 'start_of_month',
      external_end_offset_type: 'end_of_month',
      period_start_offset_type: 'start_of_month',
      period_end_offset_type: 'end_of_month',
      internal_start_date_before: false,
      internal_end_date_before: false,
      external_start_date_before: false,
      external_end_date_before: false,
      period_start_date_before: false,
      period_end_date_before: false,
    } as RepeatRules,
    default_billing_group: '',
    all_team_members: true,
    assignment_settings: 3, // Tasks Auto Assigned (see assignmentSettingsOptions)


    // New fields for enhanced project management

    period_start_date: "2023-01-07",
    period_end_date: "2023-01-07",


    // Offset unit properties

    project_status: "in-house", // 'in-house' | 'external'
    current_step_name: "",
    internal_status: "", // Planning, In Progress, Review, etc.
    client_team_member: null,
    team_leader: null,
    active_step_employee: null,
    sync_client_dates: true

  }
  project_default = "";

  add_tag = {
    title: '',
    colour: '#90ee90'
  }
  from_current_template = false;

  @ViewChild('chatContainer') private chatContainer: ElementRef;
  @ViewChild(ClientSchedulePreviewComponent) private schedulePreviewRef: ClientSchedulePreviewComponent;

  // AI Chatbot
  chatMessages: { role: 'user' | 'assistant'; content: string }[] = [];
  chatInput = '';
  isChatLoading = false;
  chatLoadingMessage = '';
  aiChatUsage = 0;
  aiChatLimit = 50;
  aiChatLimitReached = false;
  rowColourPresets: string[] = ['#FFFFFF', '#90caf9', '#ffe082', '#81c784', '#f8bbd0', '#4dd0e1', '#ce93d8', '#dce775', '#ffb74d', '#a5d6a7'];
  customRowColour = '#FFFFFF';
  isCustomRowColourSelected = false;

  private _template_example_date: string | null = null;
  editing_example_date = false;

  get template_example_date(): string {
    if (this._template_example_date) return this._template_example_date;
    const r = this.project?.repeat_rules;
    if (r?.relative_date_type === 'year_end_date') {
      return dayjs().month(9).date(31).format('YYYY-MM-DD');
    }
    return dayjs().format('YYYY-MM-DD');
  }

  set template_example_date(val: string) {
    this._template_example_date = val;
  }
  private readonly chatLoadingMessages = [
    'Thinking...', 'Crunching the numbers...', 'Consulting the AI oracle...', 'Mapping out your project...',
    'Connecting the dots...', 'Analyzing your requirements...', 'Crafting the perfect plan...',
    'Building something great...', 'Processing your vision...', 'Organizing the chaos...',
    'Almost there...', 'Weaving the magic...', 'Putting on my thinking cap...', 'Decoding your goals...'
  ];
  private chatLoadingInterval: any;

  get canSendAiChat(): boolean {
    return !this.isChatLoading && !this.aiChatLimitReached && !!this.chatInput.trim();
  }

  // Tracks what step is before "Project Schedule" so the back button is correct
  get aiStepPreviousStep(): string {
    return this.items.some(i => i.label === 'Chat with AI') ? 'Chat with AI' : 'Get Started';
  }

  constructor(private apiService: ApiService, private modalController: ModalController, public globalService: GlobalService, private projectsService: ProjectsService, private alertController: AlertController, private cdr: ChangeDetectorRef) {

    this.GetCompanies();
    this.GetClients();
    this.GetTeamMembers();
    this.GetClientsRoles();
    // (Project templates aren't loaded here - nothing on this page uses them, and they're every template's full JSON)
    this.GetTaskTemplates();
    this.GetBillingGroups();
    this.GetProjectInternalStatuses();
    this.GetAllGroups();
    this.GetAllTags();
    if (!this.edit_template_mode) {
      this.project.repeat_rules.repeat_start_date = dayjs().format('YYYY-MM-DD');
      this.onUpdateDates(this.project);
      // this.project.internal_start_date = dayjs().format('YYYY-MM-DD');
      // this.project.internal_end_date = dayjs().add(7, 'day').format('YYYY-MM-DD');
      // this.project.external_start_date = dayjs().add(1, 'day').format('YYYY-MM-DD');
      // this.project.external_end_date = dayjs().add(5, 'day').format ('YYYY-MM-DD');
      // this.project.period_start_date = dayjs().add(0, 'day').format('YYYY-MM-DD');
      // this.project.period_end_date = dayjs().add(20, 'day').format('YYYY-MM-DD');
    }

    this.project_default = JSON.stringify(this.project);
  }


  async ngOnInit() {
    this.items = [
      { label: 'Get Started' },
      { label: 'Details and Assignments' },
      { label: 'Project Schedule' },
      { label: 'Add Tasks' },
      { label: 'Review' }
    ];

    if (this.edit_template_mode) {
      this.items = this.items.filter(item => item.label !== 'Get Started' && item.label !== 'Project Schedule');
      this.GoToStep('Details and Assignments');
      this.project.assigned_companies = [];
      this.project.assigned_clients = [];
      this.project.create_template = false;
      this.GetProjectTasks();
    }

    if (this.edit_schedule_mode) {
      await this.globalService.presentLoading('Checking for modified projects...', 99999999);
      await this.getChangedProjects();
      await this.globalService.dismissLoading();
      if (this.changed_projects.length > 0) {
        await this.showScheduleEditWarning();
      }
      this.related_schedules = (this.grouped_schedules_info || []).filter((s: any) => s.guid !== this.project.schedule_guid);
    }

    if (this.preselected_template) {
      // No "Get Started" step - the template's already chosen, so jump straight to the form.
      this.items = this.items.filter(item => item.label !== 'Get Started');
      await this.globalService.presentLoading('Loading template and assigning contacts/companies...', 99999999);
      // Same path CreateFromKeyfile()'s modal dismiss handler uses, just skipping the manual
      // "pick a template" step since the caller already knows which one it wants.
      await this.LoadFromTemplate(this.preselected_template, false);
      this.project.assigned_clients = this.preselected_clients || [];
      this.project.assigned_companies = this.preselected_companies || [];
      await this.globalService.dismissLoading();
    }

    this.syncRowColourSelection();
  }

  private normalizeHexColour(colour: string): string {
    return (colour || '').trim().toUpperCase();
  }

  private syncRowColourSelection() {
    const projectColour = this.normalizeHexColour(this.project?.row_colour || '#FFFFFF');
    const presetMatch = this.rowColourPresets.find(c => this.normalizeHexColour(c) === projectColour);
    if (presetMatch) {
      this.project.row_colour = presetMatch;
      this.isCustomRowColourSelected = false;
      return;
    }

    this.customRowColour = this.project?.row_colour || this.customRowColour || '#FFFFFF';
    this.isCustomRowColourSelected = true;
  }

  isPresetRowColourSelected(color: string): boolean {
    return !this.isCustomRowColourSelected && this.normalizeHexColour(this.project?.row_colour) === this.normalizeHexColour(color);
  }

  onPresetRowColourSelect(color: string) {
    this.project.row_colour = color;
    this.isCustomRowColourSelected = false;
  }

  onCustomRowColourSelect() {
    this.isCustomRowColourSelected = true;
    this.project.row_colour = this.customRowColour || '#FFFFFF';
  }

  onCustomRowColourChange(color: string) {
    this.customRowColour = color || '#FFFFFF';
    if (this.isCustomRowColourSelected) {
      this.project.row_colour = this.customRowColour;
    }
  }


  getScheduleEndDateDisplay(start_date: string, end_date: string): string {
    const start = dayjs(start_date);
    const end = dayjs(end_date);
    const isOver19Years = end.diff(start, 'year') > 19;
    const withinFiveYearsOfEnd = end.diff(dayjs(), 'year') <= 5;
    if (isOver19Years && !withinFiveYearsOfEnd) return 'Never';
    return end.format('YYYY-MM-DD');
  }

  // The project template's JSON is what future occurrences get generated from (both server-side
  // when a scheduled instance is materialized, and client-side for calendar/list previews of
  // occurrences that don't exist yet) - it needs its own estimated_time/estimated_time_remaining
  // baked in rather than relying on whatever happens to be materialized later, so callers reading
  // the template JSON directly (without creating an actual project) still have the totals.
  // A template always represents a not-yet-started state, so estimated_time_remaining is reset to
  // the full total alongside estimated_time.
  RecalculateEstimatedTime() {
    const total = (this.project.tasks || []).reduce((sum: number, task: any) => sum + (Number(task?.estimated_time) || 0), 0);
    this.project.estimated_time = total;
    this.project.estimated_time_remaining = total;
  }

  // Saving a schedule's template needs schedule_templates_update (the API enforces it too)
  private CheckCanUpdateScheduleTemplates(): boolean {
    if (this.globalService.Can('schedule_templates_update')) return true;
    this.globalService.presentToastMessage('You do not have permission to update project schedule templates', false);
    return false;
  }

  private async UpdateCurrentScheduleTemplate() {
    if (!this.CheckCanUpdateScheduleTemplates()) return;
    this.RecalculateEstimatedTime();
    const payload = { ...this.project, back_date: this.back_date || null };
    const data: any = await this.apiService.put(`projects/schedule/templates/${this.project.template_guid}`, payload);
    this.globalService.presentToastMessage(data.message, data.success);
    if (data.success) this.modalController.dismiss(true);
  }

  private async UpdateAllRelatedSchedules() {
    if (!this.CheckCanUpdateScheduleTemplates()) return;
    this.RecalculateEstimatedTime();
    await this.globalService.presentLoading('Updating all related schedules...', 99999999);
    let all_success = true;
    const currentData: any = await this.apiService.put(`projects/schedule/templates/${this.project.template_guid}`, { ...this.project, back_date: this.back_date || null });
    if (!currentData.success) all_success = false;
    for (const schedule of this.related_schedules) {
      const template_guid = schedule.template_guid;
      if (!template_guid) continue;
      // Override schedule_guid so the backend updates the correct schedule's future projects.
      // Without this, the backend uses the current project's schedule_guid and finds no projects to update.
      const payload = { ...this.project, schedule_guid: schedule.guid, back_date: this.back_date || null };
      const data: any = await this.apiService.put(`projects/schedule/templates/${template_guid}`, payload);
      if (!data.success) all_success = false;
    }
    await this.globalService.dismissLoading();
    this.globalService.presentToastMessage(
      all_success ? 'All related schedules updated successfully.' : 'Some schedules failed to update.',
      all_success
    );
    if (all_success) this.modalController.dismiss(true);
  }

  async getChangedProjects() {
    try {
      const guids: string[] = this.edit_all_related && this.grouped_schedules_info.length > 0
        ? this.grouped_schedules_info.map((s: any) => s.guid)
        : [this.project.schedule_guid];

      const all: any[] = [];
      for (const guid of guids) {
        const data: any = await this.apiService.get('projects?has_changed=1&project_scheduler_guid=' + guid);
        if (data.success && data.data) {
          all.push(...data.data);
        }
      }
      this.changed_projects = all;
    } catch (error) {
      console.error('Error fetching changed projects:', error);
      this.changed_projects = [];
    }
  }

  async showScheduleEditWarning() {
    const modal = await this.modalController.create({
      component: ScheduleChangedProjectsComponent,
      cssClass: 'small-modal tall-modal secondary-modal',
      componentProps: {
        changed_projects: this.changed_projects,
        assigned_clients: this.project.assigned_clients || [],
        assigned_companies: this.project.assigned_companies || [],
      },
      backdropDismiss: false,
    });
    await modal.present();
  }

  //This function is used to get the most recent tasks if they are using a template for the project.
  async GetProjectTasks() {
    if (this.project && this.project.tasks && this.project.tasks.length > 0) {
      for (let i = 0; i < this.project.tasks.length; i++) {
        const task = this.project.tasks[i];
        if (task.template_guid) {
          const data: any = await this.apiService.get(`templates/tasks/${task.template_guid}`);
          if (data && data.data && data.data.length > 0) {
            this.project.tasks[i] = JSON.parse(data.data[0].json_str);
            if (!this.project.tasks[i].estimated_time) this.project.tasks[i].estimated_time = 30;
          }
        }
      }
    }
  }

  async GetProjectTemplates() {
    let data: any = await this.apiService.get('templates/projects');
    if (data.success) {
      this.project_templates = data.data;
    }
  }

  async GetTaskTemplates() {
    let data: any = await this.apiService.get('templates/tasks');
    if (data.success) {
      this.task_templates = data.data;
    }
  }

  async selectedBillingGroup() {
    this.project.default_billing_group = this.selected_billing_group.guid;
  }

  async GetProjectInternalStatuses() {
    this.loaded_internal_statuses = false;
    const data: any = await this.apiService.get('tags/projects_internal_status');
    if (data.success) this.projectInternalStatuses = data.data || [];
    if (!this.projectInternalStatuses) this.projectInternalStatuses = [];
    this.loaded_internal_statuses = true;
  }

  GetInternalStatusByGuid(guid: string) {
    if (!guid || !this.projectInternalStatuses?.length) return null;
    return this.projectInternalStatuses.find(s => s.guid === guid) || null;
  }

  get projectInternalStatusOptions() {
    return [
      { value: '', label: 'None', description: 'No internal status set.' },
      ...this.projectInternalStatuses.map(s => ({ value: s.guid, label: s.title, description: s.description, color: s.colour }))
    ];
  }

  async GetBillingGroups() {
    return;
    let data: any = await this.apiService.get('billing_groups');
    if (data.success) {
      this.billing_groups = data.data;
    }
  }

  async GetCompanies() {
    let data: any = await this.apiService.GetCompanies();;
    if (data.success) {
      this.companies = data.data;
    }
    this.RebuildAssignmentSuggestionsData();
  }

  async GetClients() {
    let data: any = await this.apiService.GetClients();;
    if (data.success) {
      if (!data.data) data.data = [];
      this.clients = data.data;
      this.clients_by_guid = new Map(data.data.filter((c: any) => c?.guid).map((c: any) => [c.guid, c]));
    }
    this.RebuildAssignmentSuggestionsData();
  }

  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    if (data.success) {
      this.team_members = data.data;
    }
    void this.RefreshTaskAssignableMembers();
  }

  async GetClientsRoles() {
    let data: any = await this.apiService.get('firms/clients/roles');
    if (data.success) this.clients_roles = data.data || [];
  }

  getCompanyRoleName(company: any): string {
    if (!company.role_guid) return 'all';
    const role = this.clients_roles.find(r => r.guid === company.role_guid);
    return role ? role.title : 'all';
  }

  // company.clients (from the bulk companies list GetCompanies() loads) only ever carries
  // guid/role_guid/is_default_for_role - never display_name/email - so any match found there needs
  // resolving against this.clients (the full client list GetClients() loads separately) to actually
  // show a name/email. Mirrors secure-email/create's GetCompanyDefaultContactLabel, extended with
  // the role-aware lookup projects also needs once a Client Role has been picked.
  private resolveFullClient(guid: string): any {
    if (!guid) return null;
    return this.clients_by_guid.get(guid) || null;
  }

  getEffectiveDefaultClient(company: any): any {
    if (company.role_guid && Array.isArray(company.clients)) {
      const roleMatch = company.clients.find((c: any) =>
        c.role_guid === company.role_guid && c.is_default_for_role
      );
      return roleMatch ? (this.resolveFullClient(roleMatch.guid) || roleMatch) : null;
    }
    // No role selected - fall back to the company's own default contact, preferring the
    // already-fully-resolved object (see RebuildAssignmentSuggestionsData/SelectAssignmentSuggestion)
    // over a fresh guid lookup.
    return company.default_client || this.resolveFullClient(company.default_client_guid) || null;
  }

  getCompanyDefaultContactName(company: any): string {
    return this.getEffectiveDefaultClient(company)?.display_name || '';
  }

  private RebuildAssignmentSuggestionsData() {
    const clients = Array.isArray(this.clients) ? this.clients : [];
    const companies = Array.isArray(this.companies) ? this.companies : [];

    const clientItems = clients.map((client: any) => ({
      ...client,
      is_company: false,
    }));

    const companyItems = companies.map((company: any) => ({
      ...company,
      is_company: true,
      default_client: company.default_client
        || this.resolveFullClient(company.default_client_guid)
        || null,
    }));

    this.combined_assignment_data = [...clientItems, ...companyItems];
    this.ApplyPreselectedAssignments(); // only does anything if the Details and Assignments step is showing
  }

  private preselected_applied = false;

  // Opened from a contact's / company's page: it's for a project for them, so the template options aren't offered
  get from_assignment(): boolean {
    return !this.preselected_template && ((this.preselected_clients?.length || 0) + (this.preselected_companies?.length || 0)) > 0;
  }

  // Opened from a contact's / company's Projects tab: they're picked the same way choosing them in the assignment search would,
  // so they're already selected when the Details and Assignments step shows. Waits for the contact / company lists to load.
  private ApplyPreselectedAssignments() {
    if (this.preselected_applied || this.edit_template_mode || this.preselected_template) return;
    if (this.activeIndexString !== 'Details and Assignments' || this.project.create_template) return;
    const wantedClients = this.preselected_clients || [];
    const wantedCompanies = this.preselected_companies || [];
    console.log("wanted Client",wantedClients);
    console.log("wanted companyies", wantedCompanies);
    if (!wantedClients.length && !wantedCompanies.length) { this.preselected_applied = true; return; }
    const clientItems = wantedClients.map((c: any) => this.combined_assignment_data.find((i: any) => !i.is_company && i.guid === c.guid));
    const companyItems = wantedCompanies.map((c: any) => this.combined_assignment_data.find((i: any) => i.is_company && i.guid === c.guid));
    if (clientItems.some(i => !i) || companyItems.some(i => !i)) return; // lists still loading
    this.preselected_applied = true;
    this.AssignPreselected(clientItems, companyItems);
  }

  private AssignPreselected(clientItems: any[], companyItems: any[]) {
    clientItems.forEach(item => {
      if (!(this.project.assigned_clients || []).some((c: any) => c.guid === item.guid)) {
        this.project.assigned_clients = [...(this.project.assigned_clients || []), { ...item, is_contact: true }];
      }
    });
    companyItems.forEach(item => {
      if (!(this.project.assigned_companies || []).some((c: any) => c.guid === item.guid)) {
        this.project.assigned_companies = [...(this.project.assigned_companies || []), {
          ...item, is_company: true, role_guid: '', role_level: null,
          default_client: item.default_client || this.resolveFullClient(item.default_client_guid) || null
        }];
      }
    });
    this.ChangeClientsOrTeamMembers();
  }

  GetAssignmentCompanyDefaultContactLabel(company: any): string {
    const defaultClient = this.getEffectiveDefaultClient(company);
    if (!defaultClient) return 'No default contact';
    return `${defaultClient.display_name} (${defaultClient.email || 'No Email'})`;
  }

  private ClearAssignmentSearchState() {
    this.assignment_search_query = '';
    this.assignment_suggestions = [];
    this.assignment_suggestions_active_index = -1;
    this.show_assignment_suggestions = false;
    this.assignment_search_blurred_with_text = false;
  }

  OnAssignmentSearchChange() {
    this.assignment_search_blurred_with_text = false;
    const q = (this.assignment_search_query || '').trim().toLowerCase();
    this.assignment_suggestions_active_index = -1;

    if (!q || !this.combined_assignment_data?.length) {
      this.assignment_suggestions = [];
      this.show_assignment_suggestions = false;
      return;
    }

    const selectedClientGuids = new Set((this.project.assigned_clients || []).map((client: any) => client.guid));
    const selectedCompanyGuids = new Set((this.project.assigned_companies || []).map((company: any) => company.guid));

    this.assignment_suggestions = this.combined_assignment_data.filter((item: any) => {
      if (item.is_company) {
        if (selectedCompanyGuids.has(item.guid)) return false;
        return (item.name || '').toLowerCase().includes(q);
      }

      if (selectedClientGuids.has(item.guid)) return false;
      return (item.display_name || '').toLowerCase().includes(q)
        || (item.email || '').toLowerCase().includes(q);
    }).slice(0, 15);

    this.show_assignment_suggestions = this.assignment_suggestions.length > 0;
  }

  OnAssignmentInputFocus() {
    this.assignment_search_blurred_with_text = false;
    if (this.assignment_search_query) this.OnAssignmentSearchChange();
  }

  OnAssignmentInputBlur() {
    this.show_assignment_suggestions = false;
    this.assignment_search_blurred_with_text = !!this.assignment_search_query?.trim();
  }

  OnAssignmentInputKeydown(event: KeyboardEvent) {
    if (!this.show_assignment_suggestions || this.assignment_suggestions.length === 0) {
      if (event.key === 'Enter' && this.assignment_search_query?.trim()) {
        event.preventDefault();
        this.assignment_search_blurred_with_text = true;
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.assignment_suggestions_active_index = Math.min(this.assignment_suggestions_active_index + 1, this.assignment_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.assignment_suggestions_active_index = Math.max(this.assignment_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.assignment_suggestions_active_index >= 0 ? this.assignment_suggestions_active_index : 0;
      this.SelectAssignmentSuggestion(this.assignment_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_assignment_suggestions = false;
    }
  }

  SelectAssignmentSuggestion(item: any) {
    if (!item?.guid) return;

    if (item.is_company) {
      if ((this.project.assigned_companies || []).some((company: any) => company.guid === item.guid)) {
        this.ClearAssignmentSearchState();
        return;
      }

      const company = {
        ...item,
        is_company: true,
        role_guid: '',
        role_level: null,
        default_client: item.default_client
          || this.resolveFullClient(item.default_client_guid)
          || null,
      };
      this.project.assigned_companies = [...(this.project.assigned_companies || []), company];
    } else {
      if ((this.project.assigned_clients || []).some((client: any) => client.guid === item.guid)) {
        this.ClearAssignmentSearchState();
        return;
      }

      this.project.assigned_clients = [...(this.project.assigned_clients || []), { ...item, is_contact: true }];
    }

    this.ClearAssignmentSearchState();
    this.ChangeClientsOrTeamMembers();
  }

  GoToStep(step) {
    this.activeIndex = this.items.findIndex(i => i.label === step);
    this.activeIndexString = step;
    // Whatever the step before did to the project (loading a template, blank project, ...) has finished by the time this runs
    if (step === 'Details and Assignments') {
      this.preselected_applied = false;
      setTimeout(() => this.ApplyPreselectedAssignments());
    }
  }

  OpenAiChat(save_as_template) {
    this.created_with_ai = true;
    this.save_as_template_ai_override = save_as_template;
    this.project.create_template = save_as_template;
    if (this.project.create_template) {
      const dummy: any = {
        display_name: 'Example Client',
        guid: 'template-preview-dummy',
        active: 1,
        year_end_date: "2026-10-31T00:00:00"
      };
      this.project.assigned_clients = [dummy];
    }
    this.loadAiChatUsage();
    if (this.chatMessages.length === 0) {
      this.chatMessages.push({
        role: 'assistant',
        content: `Hi! I'm your AI project assistant. Describe the project you'd like to create. Its best if you include the following information.\n
        - Project title and description
        - Project start and end dates
        - Should the project repeat? If so, how often should it repeat and when should the repeat end?
        - Should the project be internal only?
        - Should the tasks be forced to be completed in order or can they be done in any order?
        - Tasks and Actions that should be included in the project along with any details.\n\nDon't worry about getting everything perfect, we can adjust the details later. Just give me a general idea of what you want and I'll help you build it!`
      });
    }
    this.GoToStep('Chat with AI');
    this.scrollChatToBottom();
  }

  onChatKeydown(event: KeyboardEvent) {
    // if (event.key === 'Enter' && !event.shiftKey) {
    //   event.preventDefault();
    //   this.SendChatMessage();
    // }
  }

  async SendChatMessage() {
    const text = this.chatInput.trim();
    if (!text || this.isChatLoading || this.aiChatLimitReached) return;

    this.chatMessages.push({ role: 'user', content: text });
    this.scrollChatToBottom();
    this.chatInput = '';
    this.isChatLoading = true;
    this.startChatLoadingMessages();

    try {
      const response = await this.callAiApi(this.chatMessages);
      if (response.project) {
        this.project = { ...this.project, ...response.project };
        this.syncRowColourSelection();
      }
      this.chatMessages.push({ role: 'assistant', content: response.message });
      this.aiChatUsage = Number(response.chat_limit);
      console.log("New Usage ", this.aiChatUsage);
      this.aiChatLimitReached = this.aiChatUsage >= this.aiChatLimit;
      this.scrollChatToBottom();
    } catch (err) {
      const statusCode = Number(err?.status_code ?? err?.status ?? 0);
      if (statusCode === 429) {
        this.aiChatLimitReached = true;
        this.chatMessages.push({
          role: 'assistant',
          content: `You have reached your AI chat limit for today (${this.aiChatUsage}/${this.aiChatLimit}). Please try again later.`
        });
      } else {
        this.chatMessages.push({ role: 'assistant', content: 'Sorry, something went wrong. Please try again.' });
      }
      this.scrollChatToBottom();
    } finally {
      this.isChatLoading = false;
      this.stopChatLoadingMessages();
      await this.loadAiChatUsage();
    }
  }

  scrollChatToBottom() {
    console.log("Scroll to bottom");
    setTimeout(() => {
      const el = this.chatContainer?.nativeElement ?? document.querySelector('.ai-chat-messages');
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    }, 50);
  }

  private startChatLoadingMessages() {
    this.chatLoadingMessage = this.chatLoadingMessages[0];
    let index = 1;
    this.chatLoadingInterval = setInterval(() => {
      this.chatLoadingMessage = this.chatLoadingMessages[index % this.chatLoadingMessages.length];
      index++;
    }, 3000);
  }

  private stopChatLoadingMessages() {
    if (this.chatLoadingInterval) {
      clearInterval(this.chatLoadingInterval);
      this.chatLoadingInterval = null;
    }
    this.chatLoadingMessage = '';
  }

  private async callAiApi(messages: { role: string; content: string }[]): Promise<{ message: string; project?: any; chat_limit: string }> {
    const data: any = await this.apiService.post('ai/project-chat', { messages, current_project: JSON.stringify(this.project) });
    if (!data?.success) {
      throw data;
    }
    return { message: data.message, project: data.project ?? null, chat_limit: data.chat_limit ?? '0' };
  }

  private async loadAiChatUsage() {
    try {
      const data: any = await this.apiService.get('ai/limit');
      console.log("AI Limit Data:", data);
      if (data?.success) {
        this.aiChatUsage = data.data[0].count;
        this.aiChatLimit = data.data[0].limit;
        this.aiChatLimitReached = this.aiChatUsage >= this.aiChatLimit;
      }
    } catch (error) {
      console.error('Error loading AI chat usage:', error);
    }
  }



  UseAiProject() {
    this.GoToStep('Details and Assignments');
    this.project.create_template = this.save_as_template_ai_override;
    if (this.project.create_template) {
      const dummy: any = {
        display_name: 'Example Client',
        guid: 'template-preview-dummy',
        active: 1,
        year_end_date: "2026-10-31T00:00:00"
      };
      this.project.assigned_clients = [dummy];
    }
  }

  ChangeTaskExpand(event, task) {
    event.stopPropagation();
    task.expanded = !task.expanded;
  }


  async CreateProjectTemplate(save_as_template, master_guid, message) {
    this.RecalculateEstimatedTime();
    this.project.master_guid = master_guid;
    if (save_as_template) {
      this.project.template_visible = 1;
      this.project.assigned_clients = [];
      this.project.assigned_companies = [];
    }
    else this.project.template_visible = 0;
    this.project.validate_tasks = true;
    await this.globalService.presentLoading(message, 99999999999);
    console.log("Creating Project Template:", this.project);
    let data: any = await this.apiService.post('templates/projects', this.project);
    await this.globalService.dismissLoading();
    //Loop through each client or company and create the project for them
    if (data.success) {
      return data;
    }
    else {
      this.globalService.presentToastMessage(data.message, false);
      return null;
    }
  }

  CheckIfProjectValid() {
    let error_message = '';
    if (this.project.tasks.length == 0) error_message += 'You must have at least one task in the project.\n';
    if (!this.project.title || this.project.title.length <= 0) error_message += 'You must have a title for the project.\n';
    if (this.HasProjectDateConflict()) error_message += 'You have a date conflict with the project dates.\n';

    if (!this.edit_template_mode && !this.project.create_template && this.project.assigned_clients.length == 0 && this.project.assigned_companies.length == 0) error_message += 'You must assign at least one contact or company to the project.\n';
    if (!this.edit_template_mode && !this.project.create_template && this.project.team_leader == '') error_message += 'You must assign a team leader to the project.\n';
    if (this.project.is_internal) {
      // Check if any step in any task is a client step type
      for (const task of this.project.tasks) {
        for (const step of task.steps) {
          if (ClientStepTypes.includes(Number(step.type))) {
            error_message += 'This project cannot be internal as it has actions that require client access.\n';
            break;
          }
        }
      }
    }

    //Check if the project has tasks with steps that require the client to be active.


    return error_message;
  }

  TaskHasActiveSteps(task: any) {
    if (task.steps && task.steps.length > 0) {
      return task.steps.some((step: any) => ClientStepTypes.includes(Number(step.type)));
    }
    return false;
  }
  HasClientActiveSteps() {
    let active_steps = false;
    this.project.tasks.forEach(task => {
      active_steps = this.TaskHasActiveSteps(task);
    });
    return active_steps;
  }

  AllUsersActive() {
    let all_active = true;
    this.project.assigned_clients.forEach(client => {
      if (client.active == 0) {
        all_active = false;
      }
    });
    this.project.assigned_companies.forEach(company => {
      if (company.default_client.active == 0) {
        all_active = false;
      }
    });
    return all_active;
  }

  // The client (external) date fields are hidden, so a non-repeating project's client dates always follow
  // its project (internal) start/end dates - regardless of the sync_client_dates a template carried, since
  // there's no longer any way to edit them separately.
  SyncClientDatesToProjectDates(inc_project: any) {
    if (inc_project.internal_start_date) inc_project.external_start_date = dayjs(inc_project.internal_start_date).format("YYYY-MM-DD");
    if (inc_project.internal_end_date) inc_project.external_end_date = dayjs(inc_project.internal_end_date).format("YYYY-MM-DD");
  }

  async CreateProjects() {
    // Use Web Locks API to prevent Chrome from throttling this tab when in background
    if ('locks' in navigator) {
      await (navigator as any).locks.request('create-projects-lock', { mode: 'exclusive' }, async () => {
        await this.executeCreateProjects();
      });
    } else {
      // Fallback for browsers without Web Locks API
      await this.executeCreateProjects();
    }
  }

  private async executeCreateProjects() {
    if (!this.project.repeat) this.SyncClientDatesToProjectDates(this.project);
    // Propagate project-level assignment settings to all tasks before creation
    (this.project.tasks || []).forEach((task: any) => this.ApplyTaskAssignment(task));
    // Covers the one-off (not saved as a template, not repeating) path too, where
    // CreateProjectTemplate() below never runs - project_copy still needs the totals.
    this.RecalculateEstimatedTime();
    let was_success = true;
    let master_guid = this.globalService.createGUID();
    if (this.project.create_template || this.project.repeat) {
      let template_data = await this.CreateProjectTemplate(this.project.create_template, master_guid, 'Creating Project Template.');
      if (!template_data.success) was_success = false;
    }

    //Only do this code if we are not creating the template
    if (!this.project.create_template) {
      let total_to_create = this.project.assigned_clients.length + this.project.assigned_companies.length;
      let total_created = 0;
      for (let i = 0; i < this.project.assigned_clients.length; i++) {
        let schedule_guid = this.globalService.createGUID();
        let project_template_guid = null;
        if (this.project.repeat) {
          let template_data = await this.CreateProjectTemplate(false, master_guid, "Creating for : " + this.project.assigned_clients[i].display_name);
          if (template_data.success) project_template_guid = template_data.data;
          else was_success = false;
        }
        await this.globalService.presentLoading("Creating for : " + this.project.assigned_clients[i].display_name + " (" + (total_created + 1) + " of " + total_to_create + ")", 99999999999);
        var client = this.project.assigned_clients[i];
        let project_copy = this.globalService.MakeJSONCopy(this.project);
        //For each client we want to set the project client id
        project_copy.company = null;
        project_copy.client = client;
        console.log("PROJECT: ", JSON.stringify(this.project));
        if (this.project.repeat) {
          let schedule_data: any = await this.CreateProjectScheduler(schedule_guid, master_guid, project_template_guid, client, null, project_copy);
        }

        if (!project_copy.repeat) {
          project_copy.project_scheduler_guid = null;
          let data: any = await this.apiService.post('projects', project_copy);
        }

        await this.globalService.dismissLoading();
        total_created++;
      }

      for (let i = 0; i < this.project.assigned_companies.length; i++) {
        let schedule_guid = this.globalService.createGUID();
        let project_template_guid = null;
        var company = this.project.assigned_companies[i];
        if (this.project.repeat) {
          this.project.specific_role = 0;
          this.project.role_guid = company.role_guid;
          this.project.role_level = company.role_level;

          let template_data = await await this.CreateProjectTemplate(false, master_guid, "Creating for : " + this.project.assigned_companies[i].name);
          if (template_data.success) project_template_guid = template_data.data;
          else was_success = false;
        }
        await this.globalService.presentLoading("Creating for : " + this.project.assigned_companies[i].name + " (" + (total_created + 1) + " of " + total_to_create + ")", 99999999999);

        let project_copy = this.globalService.MakeJSONCopy(this.project);
        //For each client we want to set the project client id
        project_copy.client = null;
        project_copy.company = company;
        project_copy.specific_role = 0;
        project_copy.role_guid = company.role_guid;
        project_copy.role_level = company.role_level;

        if (this.project.repeat) {
          let schedule_data: any = await this.CreateProjectScheduler(schedule_guid, master_guid, project_template_guid, null, company, project_copy);
        }
        if (!project_copy.repeat) {
          let data: any = await this.apiService.post('projects', project_copy);
        }

        await this.globalService.dismissLoading();
        total_created++;
      }
    }
    if (was_success) this.modalController.dismiss(true);
  }

  async RepeatToJson() {

    // Also show what the next create date would be
    if (this.project.repeat && this.project.repeat_rules) {
      try {
        const anchorISO = this.project.internal_start_date;
        const windowStart = dayjs().subtract(1, 'day').toISOString();
        const windowEnd = dayjs().add(1, 'year').toISOString();

        const occurrences = this.projectsService.buildOccurrences(
          this.project.repeat_rules,
          anchorISO,
          windowStart,
          windowEnd
        );


        const today = dayjs().startOf('day');
        const nextOccurrence = occurrences.find(occ => occ.startOf('day').isAfter(today) || occ.startOf('day').isSame(today));
      } catch (error) {
        console.error('Error testing repeat rules:', error);
      }
    }
  }


  async CreateProjectScheduler(schedule_guid, master_guid, project_template_guid, client, company, project_copy) {
    //let next_create_date = project_copy.internal_start_date;
    let start_date = this.globalService.MakeJSONCopy(project_copy).repeat_rules.repeat_start_date;
    console.log("Start Date:", start_date);
    if (project_copy.repeat_rules.relative_date_type == 'year_end_date') {
      if (project_copy.repeat_rules.frequency == 'monthly' || project_copy.repeat_rules.frequency == 'quarterly' || project_copy.repeat_rules.frequency == 'semi-annually') {
        let year_end_date = dayjs();
        if (client) year_end_date = dayjs(client.year_end_date);
        else year_end_date = dayjs(company.year_end_date);
        const yearEndMonth = year_end_date.month();
        const yearEndDay = year_end_date.date(); ``
        const current_year = dayjs().year();
        const this_year_client_year_end = dayjs().year(current_year).month(yearEndMonth).date(yearEndDay);



        // If this year's year-end date is after the repeat start date, work backwards
        if (this_year_client_year_end.isAfter(dayjs(project_copy.repeat_rules.repeat_start_date))) {
          // Calculate how many intervals back we need to go
          const monthsDiff = this_year_client_year_end.diff(dayjs(project_copy.repeat_rules.repeat_start_date), 'month');
          const intervalsBack = Math.ceil(monthsDiff / project_copy.repeat_rules.interval);

          // Set next_create_date to the earliest valid occurrence
          project_copy.repeat_rules.repeat_start_date = this_year_client_year_end.subtract(intervalsBack * project_copy.repeat_rules.interval, 'month').format("YYYY-MM-DD");
        } else {
          // Use this year's year-end date as the base
          project_copy.repeat_rules.repeat_start_date = this_year_client_year_end.format("YYYY-MM-DD");
        }

        project_copy.repeat_rules.monthly_type = 'day';
        project_copy.repeat_rules.monthly_day = yearEndDay;

      }
      else if (project_copy.repeat_rules.frequency == 'yearly') {
        let year_end_date = dayjs();
        if (client) year_end_date = dayjs(client.year_end_date);
        else year_end_date = dayjs(company.year_end_date);

        const yearEndMonth = year_end_date.month();
        const yearEndDay = year_end_date.date();
        const current_year = dayjs().year();
        const this_year_client_year_end = dayjs().year(current_year).month(yearEndMonth).date(yearEndDay);

        // If this year's year-end date is after the repeat start date, work backwards
        if (this_year_client_year_end.isAfter(dayjs(project_copy.repeat_rules.repeat_start_date))) {
          // Calculate how many intervals back we need to go
          const yearInterval = Math.max(1, project_copy.repeat_rules.interval || 1);
          const yearsDiff = this_year_client_year_end.diff(dayjs(project_copy.repeat_rules.repeat_start_date), 'year');
          const intervalsBack = Math.ceil(yearsDiff / yearInterval);
          project_copy.repeat_rules.repeat_start_date = this_year_client_year_end.subtract(intervalsBack * yearInterval, 'year').format("YYYY-MM-DD");
        } else {
          // Use this year's year-end date as the base
          project_copy.repeat_rules.repeat_start_date = this_year_client_year_end.format("YYYY-MM-DD");
        }

        project_copy.repeat_rules.yearly_month = yearEndMonth + 1; // dayjs months are 0-indexed, yearly_month is 1-indexed
        project_copy.repeat_rules.yearly_day = yearEndDay;
        project_copy.repeat_rules.yearly_use_ordinal = false;

      }

      // Advance repeat_start_date forward by intervals until it is on or after the original start_date.
      // The year-end logic may have placed repeat_start_date in the past (e.g. last year's year-end),
      // but we must not begin repeating before the user's intended start.
      const startDateDj = dayjs(start_date);
      let rsd = dayjs(project_copy.repeat_rules.repeat_start_date);
      if (rsd.isBefore(startDateDj)) {
        const unit = project_copy.repeat_rules.frequency === 'yearly' ? 'year' : 'month';
        const diff = startDateDj.diff(rsd, unit);
        const steps = Math.ceil(diff / project_copy.repeat_rules.interval);
        rsd = rsd.add(steps * project_copy.repeat_rules.interval, unit);
        if (rsd.isBefore(startDateDj)) rsd = rsd.add(project_copy.repeat_rules.interval, unit);
        project_copy.repeat_rules.repeat_start_date = rsd.format('YYYY-MM-DD');
      }
      start_date = project_copy.repeat_rules.repeat_start_date;

      this.onUpdateDates(project_copy);
    }
    const intStartMod = project_copy.repeat_rules.internal_start_date_before ? -1 : 1;
    let next_create_date_dj = dayjs(project_copy.repeat_rules.repeat_start_date)
      .add(project_copy.repeat_rules.internal_start_offset * intStartMod, project_copy.repeat_rules.internal_start_offset_unit);
    if (project_copy.repeat_rules.internal_start_offset_type === 'start_of_month') {
      next_create_date_dj = next_create_date_dj.startOf('month');
    } else if (project_copy.repeat_rules.internal_start_offset_type === 'end_of_month') {
      next_create_date_dj = next_create_date_dj.endOf('month');
    }
    let next_create_date = next_create_date_dj.format("YYYY-MM-DD");


    let repeat_end_date = '';
    if (project_copy.repeat_rules.end_type == 'never') repeat_end_date = dayjs().add(20, 'year').format('YYYY-MM-DD');
    else if (project_copy.repeat_rules.end_type == 'on') repeat_end_date = dayjs(project_copy.repeat_rules.end_date).format('YYYY-MM-DD');
    else if (project_copy.repeat_rules.end_type == 'after') {
      //Get the last occurrence based on the rules and set the end date
      const windowEnd = dayjs().add(50, 'year').toISOString();
      const occurrences = this.projectsService.buildOccurrences(
        project_copy.repeat_rules,
        project_copy.repeat_rules.repeat_start_date,
        project_copy.repeat_rules.repeat_start_date,
        windowEnd
      );
      repeat_end_date = occurrences[occurrences.length - 1].format("YYYY-MM-DD");
    }
    let company_guid = null;
    if (company) company_guid = company.guid;
    let client_guid = null;
    if (client) client_guid = client.guid;
    let role_guid = "";
    let role_level = "0";
    let specific_role = 0;
    if (company) {
      project_copy.role_guid = company.role_guid;
      project_copy.role_level = company.role_level;
      project_copy.specific_role = 0;
    }


    console.log("START DATE:", start_date);
    var schedule_info = {
      time_created_guid: schedule_guid,
      master_guid: master_guid,
      project_template_guid: project_template_guid,
      next_create_date: next_create_date,
      start_date: dayjs(start_date).format('YYYY-MM-DD'),
      end_date: repeat_end_date,
      pattern: project_copy.repeat_rules.pattern,
      interval: project_copy.repeat_rules.interval,
      day_of_week: '',
      day_of_month: '',
      repeat_on_start: true,
      client_guid: client_guid,
      company_guid: company_guid,
      project: project_copy,
      repeat_rules: JSON.stringify(project_copy.repeat_rules),
      role_guid: role_guid,
      role_level: role_level,
      specific_role: specific_role
    }
    let data: any = await this.apiService.post('projects_schedule', schedule_info);
    return data;
  }

  /**
   * Fallback method to calculate next create date using simple interval-based logic
   */
  private calculateFallbackNextDate(): string {
    const rules = this.project.repeat_rules;
    const startDate = dayjs(this.project.internal_start_date);
    const today = dayjs().startOf('day');

    let nextDate = startDate;

    // If start date is in the future, use it as the next date
    if (startDate.isAfter(today)) {
      return startDate.format('YYYY-MM-DD');
    }

    // Otherwise, calculate based on frequency and interval
    switch (rules.frequency) {
      case 'daily':
        nextDate = today.add(rules.interval, 'day');
        break;
      case 'weekly':
        nextDate = today.add(rules.interval, 'week');
        break;
      case 'monthly':
        nextDate = today.add(rules.interval, 'month');
        break;
      case 'yearly':
        nextDate = today.add(rules.interval, 'year');
        break;
      default:
        // Default to daily if frequency is unknown
        nextDate = today.add(1, 'day');
    }

    return nextDate.format('YYYY-MM-DD');
  }

  async Close() {
    const isDirty = this.project.title?.trim()
      || this.project.tasks?.length > 0
      || this.project.assigned_clients?.length > 0
      || this.project.assigned_companies?.length > 0;
    if (isDirty) {
      const alert = await this.alertController.create({
        header: 'Are you sure?',
        message: 'You have unsaved changes. If you cancel now, your work will be lost.',
        buttons: [
          { text: 'Keep Editing', role: 'cancel' },
          { text: 'Discard', role: 'destructive', handler: () => this.modalController.dismiss(null) }
        ]
      });
      await alert.present();
    } else {
      this.modalController.dismiss(null);
    }
  }

  async OpenCreateTaskModal() {
    const modal = await this.modalController.create({
      component: CreateTaskPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        project: this.project,
        create_edit_mode: true,
        edit_mode: false,
        assignable_member_guids: this.task_assignable_member_guids
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.GetTaskTemplates();
        const newTask = this.globalService.MakeJSONCopy(data.data);
        this.ApplyTaskAssignment(newTask);
        if (!newTask.estimated_time) newTask.estimated_time = 30;
        this.project.tasks.push(newTask);
        this.ChangeProjectDates(true);
      }
    });
    return await modal.present()
  }

  //@TODO finish this
  async RemoveGroup(group) {
    //Find the group in the project and remove it.
    this.globalService.RemoveFromArray(this.project.groups, group.guid);
  }

  async GetAllGroups() {
    const data: any = await this.apiService.GetGroups('projects');
    this.all_groups = data?.data || [];
  }

  OnGroupSearchChange() {
    const q = (this.group_search_query || '').trim().toLowerCase();
    this.group_suggestions_active_index = -1;

    if (!q || !this.all_groups?.length) {
      this.group_suggestions = [];
      this.show_group_suggestions = false;
      return;
    }

    const selectedGuids = new Set((this.project.groups || []).map((g: any) => g.guid));
    this.group_suggestions = this.all_groups
      .filter((g: any) => !selectedGuids.has(g.guid) && (g.title || '').toLowerCase().includes(q))
      .slice(0, 15);

    this.show_group_suggestions = this.group_suggestions.length > 0;
  }

  OnGroupInputFocus() {
    if (this.group_search_query) this.OnGroupSearchChange();
  }

  OnGroupInputBlur() {
    this.show_group_suggestions = false;
  }

  OnGroupInputKeydown(event: KeyboardEvent) {
    if (!this.show_group_suggestions || this.group_suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.group_suggestions_active_index = Math.min(this.group_suggestions_active_index + 1, this.group_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.group_suggestions_active_index = Math.max(this.group_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.group_suggestions_active_index >= 0 ? this.group_suggestions_active_index : 0;
      this.SelectGroupSuggestion(this.group_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_group_suggestions = false;
    }
  }

  SelectGroupSuggestion(group: any) {
    if (!group?.guid) return;
    if ((this.project.groups || []).some((g: any) => g.guid === group.guid)) {
      this.ClearGroupSearchState();
      return;
    }
    this.project.groups = [...(this.project.groups || []), group];
    this.ClearGroupSearchState();
  }

  private ClearGroupSearchState() {
    this.group_search_query = '';
    this.group_suggestions = [];
    this.group_suggestions_active_index = -1;
    this.show_group_suggestions = false;
  }

  async RemoveTask(task) {
    const alert = await this.alertController.create({
      header: 'Remove Task',
      message: 'Are you sure you want to remove this task? ' + task.title,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Remove',
          handler: () => {
            const index = this.globalService.GetArrayIndex(this.project.tasks, task.guid);
            if (index !== -1)
              this.project.tasks.splice(index, 1);
            this.RecalculateEstimatedTime();
          }
        }
      ]
    });
    await alert.present();
  }

  async EditTask(task) {
    task.checked_details = true;
    const modal = await this.modalController.create({
      component: CreateTaskPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        project: this.project,
        task: task,
        edit_mode: false,
        create_edit_mode: true,
        assignable_member_guids: this.task_assignable_member_guids
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.GetTaskTemplates();
        this.ChangeProjectDates(true);
      }
    });
    return await modal.present()
  }

  async RemoveStep(task, step) {
    //Find the step in the task and remove it.
    this.globalService.RemoveFromArray(task.steps, step.guid);
    this.ProjectClientAccessCheck();
  }

  async ClearClient() {
    this.project.client = {};
  }
  async ClearCompany() {
    this.project.company = {};
  }

  async CreateFromKeyfile(save_as_template) {
    const modal = await this.modalController.create({
      component: TemplateBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_template_selection: true,
        template_select_mode: false,
        template_type: 'project'
      }
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.LoadFromTemplate(data.data, save_as_template);
      }
    });

    await modal.present();

  }

  // Pick an existing project template and change it in place. Unlike CreateFromKeyfile(true), which copies a template into a
  // brand new one, this opens the template in the same edit screen the Manage Project Templates page uses
  // (edit_template_mode) whose Update Template button saves over the original (PUT templates/projects/{guid}).
  // Changing a saved template changes what every future project made from it looks like, so it needs templates_update
  // (admins always have it; the API enforces this too)
  get canUpdateTemplates(): boolean {
    return this.globalService.Can('project_templates_update');
  }

  async UpdateExistingTemplate() {
    if (!this.canUpdateTemplates) {
      this.globalService.presentToastMessage('You do not have permission to update existing templates.', false);
      return;
    }
    const picker = await this.modalController.create({
      component: TemplateBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_template_selection: true,
        template_select_mode: false,
        template_type: 'project',
        hide_our_templates: true
      }
    });

    picker.onDidDismiss().then(async (data: any) => {
      if (!data.data) return;
      const project = JSON.parse(data.data.json_str);
      (project.tasks || []).forEach((t: any) => {
        if (!t.estimated_time) t.estimated_time = 30;
        // Same clean-up as LoadFromTemplate: older templates can carry a nested snapshot of the whole project on each task,
        // which would otherwise be saved back into the template and grow every time it's updated
        delete t.project;
      });
      project.template_guid = data.data.guid;

      const editor = await this.modalController.create({
        component: CreatePage,
        cssClass: 'large-modal secondary-modal',
        backdropDismiss: false,
        componentProps: {
          project: project,
          edit_template_mode: true
        }
      });
      // Once the template (and anything updated with it) is done, close this create screen too. Cancelling the editor
      // leaves it open.
      editor.onDidDismiss().then((result: any) => {
        if (result?.data === true) this.modalController.dismiss(true);
      });
      await editor.present();
    });

    await picker.present();
  }

  async LoadFromTemplate(template, save_as_template) {

    this.from_current_template = true;
    this._template_example_date = null;
    this.ClearAssignmentSearchState();
    this.selected_project_template = template;
    this.project = JSON.parse(this.selected_project_template.json_str);

    (this.project.tasks || []).forEach((t: any) => {
      if (!t.estimated_time) t.estimated_time = 30;
      // Older templates can carry a full nested snapshot of the project on each task (a display-only
      // field the shared task table hydrates elsewhere). Left in place it re-copies itself into every
      // task on every future save, so a few generations of "save as template" can blow the JSON up to
      // megabytes and make JSON.parse/stringify visibly hang the page. It isn't used on this page.
      delete t.project;
    });
    this.syncRowColourSelection();
    this.project.guid = this.globalService.createGUID();
    this.project.template_guid = this.selected_project_template.guid;
    this.GoToStep('Details and Assignments');
    this.project.assigned_companies = [];
    this.project.assigned_clients = [];
    this.project.create_template = save_as_template;


    // Reset new project management fields to defaults
    this.project.project_status = "in-house";
    this.project.current_step_name = "";
    this.project.internal_status = "";
    this.project.client_team_member = null;
    this.project.team_leader = null;
    this.project.active_step_employee = null;
    this.project.repeat_rules = JSON.parse(JSON.stringify(this.selected_project_template.repeat_rules));
    this.project.repeat_rules.repeat_start_date = dayjs().format('YYYY-MM-DD');

    // Fix templates where start_of_month/end_of_month offset type was saved without a month unit
    const offsetPrefixes = ['internal_start', 'internal_end', 'external_start', 'external_end', 'period_start', 'period_end'];
    for (const prefix of offsetPrefixes) {
      const typeKey = `${prefix}_offset_type`;
      const unitKey = `${prefix}_offset_unit`;
      const t = this.project.repeat_rules[typeKey];
      if ((t === 'start_of_month' || t === 'end_of_month') && this.project.repeat_rules[unitKey] !== 'month') {
        this.project.repeat_rules[typeKey] = 'calculated_date';
      }
    }

    // Recalculate all project dates from today's repeat start date
    if (this.project.repeat && this.project.repeat_rules?.relative_date_type !== 'year_end_date') {
      this.onUpdateDates(this.project);
    }

    if (!template.is_your_template) {
      let skill_data = await this.apiService.GetTags('team_members');
      let skills = skill_data?.data || [];
      let skillsByTitle = new Map(skills.map((s: any) => [s.title, s]));
      console.log("Project Tasks", this.project.tasks);
      let skills_we_dont_have = [];
      for (let i = 0; i < this.project.tasks.length; i++) {
        const task = this.project.tasks[i];
        if (task.skills && task.skills.length > 0) {
          for (let j = 0; j < task.skills.length; j++) {
            const skill = task.skills[j];
            const foundSkill = skillsByTitle.get(skill.title);
            if (!foundSkill) {
              console.log("Skill", skill);
              skills_we_dont_have.push(skill);
            }
            else {
              task.skills[j] = foundSkill;
            }
          }
        }
      }
      if (skills_we_dont_have.length > 0) {
        const alert = await this.alertController.create({
          header: 'Missing Skills',
          message: `Some tasks have skills that are not in your system: ${skills_we_dont_have.map(s => s.title).join(', ')}. Would you like to create these skills now?`,
          buttons: [
            {
              text: 'Skip And Remove',
              role: 'cancel',
              handler: () => {
                return false;
              }
            },
            {
              text: 'Create Skills',
              handler: () => {
                return true;
              }
            }
          ]
        });

        await alert.present();
        const { role } = await alert.onDidDismiss();
        const createSkills = role !== 'cancel';
        console.log("Create Skills:", createSkills);

        if (createSkills) {
          await this.globalService.presentLoading('Creating skills...', 99999999);
          try {
            for (const skill of skills_we_dont_have) {
              const skillData = {
                title: skill.title,
                colour: skill.colour || '#90ee90'
              };
              await this.apiService.post('tags/team_members', skillData);
            }
            // Refresh skills list
            skill_data = await this.apiService.GetTags('team_members');
            skills = skill_data?.data || [];
            skillsByTitle = new Map(skills.map((s: any) => [s.title, s]));
            // Re-map skills in tasks
            for (let i = 0; i < this.project.tasks.length; i++) {
              const task = this.project.tasks[i];
              if (task.skills && task.skills.length > 0) {
                for (let j = 0; j < task.skills.length; j++) {
                  const skill = task.skills[j];
                  const foundSkill = skillsByTitle.get(skill.title);
                  if (foundSkill) {
                    task.skills[j] = foundSkill;
                  }
                }
              }
            }
            this.globalService.presentToastMessage('Skills created successfully', true);
          } finally {
            await this.globalService.dismissLoading();
          }
        }
        else {
          // Remove skills that don't exist in the system
          for (let i = 0; i < this.project.tasks.length; i++) {
            const task = this.project.tasks[i];
            if (task.skills && task.skills.length > 0) {
              task.skills = task.skills.filter(skill => skillsByTitle.has(skill.title));
            }
          }
        }
      }

      // Handle groups
      let group_data = await this.apiService.GetGroups('projects');
      let groups = group_data?.data || [];
      const groupKey = (g: any) => `${g.title}|||${g.parent_guid}`;
      let groupsByKey = new Map(groups.map((g: any) => [groupKey(g), g]));
      console.log("Project Groups", this.project.groups);
      let groups_we_dont_have = [];
      for (let i = 0; i < this.project.groups.length; i++) {
        const group = this.project.groups[i];
        const foundGroup = groupsByKey.get(groupKey(group));
        if (!foundGroup) {
          console.log("Group", group);
          groups_we_dont_have.push(group);
        }
        else {
          this.project.groups[i] = foundGroup;
        }
      }
      if (groups_we_dont_have.length > 0) {
        const alert = await this.alertController.create({
          header: 'Missing Groups',
          message: `This template has groups that are not in your system: ${groups_we_dont_have.map(g => g.title).join(', ')}. Would you like to create these groups now?`,
          buttons: [
            {
              text: 'Skip And Remove',
              role: 'cancel',
              handler: () => {
                return false;
              }
            },
            {
              text: 'Create Groups',
              handler: () => {
                return true;
              }
            }
          ]
        });

        await alert.present();
        const { role } = await alert.onDidDismiss();
        const createGroups = role !== 'cancel';
        console.log("Create Groups:", createGroups);

        if (createGroups) {
          await this.globalService.presentLoading('Creating groups...', 99999999);
          try {
            for (const group of groups_we_dont_have) {
              const groupData = {
                title: group.title,
                colour: group.colour || '#90ee90',
                parent_guid: group.parent_guid
              };
              await this.apiService.post('groups/projects', groupData);
            }
            // Refresh groups list
            group_data = await this.apiService.GetGroups('projects');
            groups = group_data?.data || [];
            groupsByKey = new Map(groups.map((g: any) => [groupKey(g), g]));
            // Re-map groups
            for (let i = 0; i < this.project.groups.length; i++) {
              const group = this.project.groups[i];
              const foundGroup = groupsByKey.get(groupKey(group));
              if (foundGroup) {
                this.project.groups[i] = foundGroup;
              }
            }
            this.globalService.presentToastMessage('Groups created successfully', true);
          } finally {
            await this.globalService.dismissLoading();
          }
        }
        else {
          // Remove groups that don't exist in the system
          this.project.groups = this.project.groups.filter((group: any) => groupsByKey.has(groupKey(group)));
        }
      }
      // this.project.groups = [];
      // //this.project.tags = [];
      // this.project.assigned_clients = [];
      // this.project.assigned_companies = [];
      if (this.project.create_template) {
        const dummy: any = {
          display_name: 'Example Client',
          guid: 'template-preview-dummy',
          active: 1,
          year_end_date: "2026-10-31T00:00:00"
        };
        this.project.assigned_clients = [dummy];
      }

    if(!this.project.assignment_settings) {
      if (this.project.tasks && this.project.tasks.length > 0) {
        this.project.assignment_settings = this.project.tasks[0].assignment_settings || 3;
      } else {
        this.project.assignment_settings = 3;
      }
      this.OnAssignmentSettingsChange();
    } 


      console.log("Project after loading template:", this.project);

    }
  }

  async LoadFromTaskTemplates() {
    if (this.selected_task_template) {
      let task_data: any = await this.apiService.get(`templates/tasks/${this.selected_task_template.guid}`);
      let task_json: any = JSON.parse(task_data.data[0].json_str);
      if (this.project.is_internal && this.projectsService.TaskHasExternalSteps(task_json)) {
        this.globalService.presentToastMessage('The selected task template has actions that require client access. Cannot add to an internal project.', false);
        return;
      }
      task_json.template_guid = this.selected_task_template.guid;
      task_json.guid = this.globalService.createGUID();
      task_json.front_end_guid = this.globalService.createGUID();
      this.ApplyTaskAssignment(task_json);
      if (!task_json.estimated_time) task_json.estimated_time = 30;
      this.project.tasks.push(task_json);
      this.ChangeProjectDates(true);
      this.selected_task_template = null;
    }
  }

  async CreateFromScratch(save_as_template) {
    this.from_current_template = false;
    this._template_example_date = null;
    this.ClearAssignmentSearchState();
    this.project = JSON.parse(this.project_default);
    this.syncRowColourSelection();
    this.selected_project_template = null;
    this.GoToStep('Details and Assignments');
    this.project.create_template = save_as_template;
    if (this.project.create_template) {
      const dummy: any = {
        display_name: 'Example Client',
        guid: 'template-preview-dummy',
        active: 1,
        year_end_date: "2026-10-31T00:00:00"
      };
      this.project.assigned_clients = [dummy];
    }
    this.created_with_ai = false;


  }


  //This function needs to loop through all tasks where the date is relative and update the date in the task.
  //Then loop through all steps in that task and update their
  async ChangeProjectDates(change_dates) {
    this.RecalculateEstimatedTime();
    this.ProjectClientAccessCheck();
    this.CheckTaskSkillCoverage();
    this.project.tasks.forEach(task => {
      let dates_valid = true;
      if (task.relative_date) {
        CalculateRelativeTaskDates(task, this.project.internal_start_date, this.project.internal_end_date);
        if (task.due_date > this.project.internal_end_date) {
          if (change_dates) {
            task.due_date = this.project.internal_end_date;
          }
          else {
            dates_valid = false;
            this.globalService.presentToastMessage(`Task: ${task.title} is due after the project is due.`, false);
          }

        }
        if (task.due_date < this.project.internal_start_date) {
          if (change_dates) {
            task.due_date = this.project.internal_start_date;
          }
          else {
            dates_valid = false;
            this.globalService.presentToastMessage(`Task: ${task.title} is due before the project is started.`, false);
          }
        }
        // Action due dates are no longer used - just keep the stored value inside its task's dates.
        task.steps.forEach(step => {
          if (step.relative_date) {
            step.due_date = dayjs(task.start_date).add(step.due_date_relative, 'day').format('YYYY-MM-DD');
            if (step.due_date > task.due_date) step.due_date = task.due_date;
            if (step.due_date < task.start_date) step.due_date = task.start_date;
          }
        });
      }
      else {
        if (task.due_date > this.project.internal_end_date) {
          dates_valid = false;
          this.globalService.presentToastMessage(`Task: ${task.title} is due after the project is due.`, false);
        }
        if (task.due_date < this.project.internal_start_date) {
          dates_valid = false;
          this.globalService.presentToastMessage(`Task: ${task.title} is due before the project is started.`, false);
        }
      }

    });

  }




  ProjectDateConfilct(date_in) {
    if ((date_in > this.project.internal_end_date) || date_in < this.project.internal_start_date) return true;

  }

  HasProjectDateConflict() {
    let has_conflict = false;
    for (let i = 0; i < this.project.tasks.length; i++) {
      let task = this.project.tasks[i];
      if (this.ProjectDateConfilct(task.due_date)) {
        has_conflict = true;
        break;
      }
    }
    return has_conflict;

  }

  //Remove from arrays
  async RemoveTag(tag) {
    this.globalService.RemoveFromArray(this.project.tags, tag.guid);
  }

  async GetAllTags() {
    const data: any = await this.apiService.GetTags('projects');
    this.all_tags = data?.data || [];
  }

  OnTagSearchChange() {
    const q = (this.tag_search_query || '').trim().toLowerCase();
    this.tag_suggestions_active_index = -1;

    if (!q || !this.all_tags?.length) {
      this.tag_suggestions = [];
      this.show_tag_suggestions = false;
      return;
    }

    const selectedGuids = new Set((this.project.tags || []).map((t: any) => t.guid));
    this.tag_suggestions = this.all_tags
      .filter((t: any) => !selectedGuids.has(t.guid) && (t.title || '').toLowerCase().includes(q))
      .slice(0, 15);

    this.show_tag_suggestions = this.tag_suggestions.length > 0;
  }

  OnTagInputFocus() {
    if (this.tag_search_query) this.OnTagSearchChange();
  }

  OnTagInputBlur() {
    this.show_tag_suggestions = false;
  }

  OnTagInputKeydown(event: KeyboardEvent) {
    if (!this.show_tag_suggestions || this.tag_suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.tag_suggestions_active_index = Math.min(this.tag_suggestions_active_index + 1, this.tag_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.tag_suggestions_active_index = Math.max(this.tag_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.tag_suggestions_active_index >= 0 ? this.tag_suggestions_active_index : 0;
      this.SelectTagSuggestion(this.tag_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_tag_suggestions = false;
    }
  }

  SelectTagSuggestion(tag: any) {
    if (!tag?.guid) return;
    if ((this.project.tags || []).some((t: any) => t.guid === tag.guid)) {
      this.ClearTagSearchState();
      return;
    }
    this.project.tags = [...(this.project.tags || []), tag];
    this.ClearTagSearchState();
  }

  private ClearTagSearchState() {
    this.tag_search_query = '';
    this.tag_suggestions = [];
    this.tag_suggestions_active_index = -1;
    this.show_tag_suggestions = false;
  }



  //Clients and companies on the project
  async presentSelectClientModal() {
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_client_selection: true,
        allow_non_client_selection: false,
        allow_company_selection: true,
        allow_contact_selection: true,
        allow_company_group_selection: false,
        allow_contact_group_selection: false,
        in_selected_companies: this.project.assigned_companies,
        in_selected_contacts: this.project.assigned_clients,
        expanded_clients: true
      },
    })
    modal.onDidDismiss().then(async (data: any) => {
      // A contact or company may have been created inside the address book - reload the lists the
      // assignment quick search works from.
      await Promise.all([this.GetCompanies(), this.GetClients()]);
      console.log("DATA DISMISSED: ", JSON.stringify(data));
      this.project.assigned_clients = [];
      this.project.assigned_companies = [];
      this.ClearAssignmentSearchState();
      if (data) {
        if (data.data) {
          data.data.forEach(element => {
            if (element.is_company) {
              this.project.assigned_companies.push(element);
            } else if (element.is_contact) {
              this.project.assigned_clients.push(element);
              console.log(this.project.assigned_clients);
            }
          });
        }
      }
      console.log("Selected Clients:", this.project.assigned_clients);
      console.log("Selected Companies:", this.project.assigned_companies);
      this.ChangeClientsOrTeamMembers();
    });
    return await modal.present()
  }

  async RemoveClient(client) {
    this.globalService.RemoveFromArray(this.project.assigned_clients, client.guid);
    this.ChangeClientsOrTeamMembers();
    this.ProjectClientAccessCheck(); // so the client access warning drops them too
  }

  async presentSelectCompaniesModal() {
    const modal = await this.modalController.create({
      component: CompaniesSelectPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        selecting_for_company: false,
        selected_clients: this.project.assigned_companies,
        in_clients: [],
        target_header: 'Currently Selected'
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.project.assigned_companies = data.data;
      }
      this.ChangeClientsOrTeamMembers();
    });
    return await modal.present()
  }

  async RemoveCompany(company) {
    this.globalService.RemoveFromArray(this.project.assigned_companies, company.guid);
    this.ChangeClientsOrTeamMembers();
    this.ProjectClientAccessCheck(); // so the client access warning drops them too
  }

  //Present the modal to select tags to apply to the company
  async presentSelectTagsModal() {
    let tags_copy = JSON.stringify(this.project.tags);
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'projects',
        selected_tags: this.project.tags
      }
    })
    modal.onDidDismiss().then((data: any) => {
      // A tag may have been created in the modal - reload the list the tag search box works from
      this.GetAllTags();

      if (data && data.data) {
        tags_copy = JSON.parse(tags_copy);
      }
      else {
        this.project.tags = [...JSON.parse(tags_copy)];
      }
    });
    return await modal.present()
  }

  async presentSelectTeamMembers() {
    let team_member_copy = JSON.stringify(this.project.team_members);
    const modal = await this.modalController.create({
      component: TeamMemberSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        selecting_for_company: false,
        selecting_for_projects: true,
        selected_team_members: this.project.team_members,
        remove_team_members: this.project.team_members,
        in_team_members: [],
        target_header: 'Currently Selected'
      }
    })
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        team_member_copy = JSON.parse(team_member_copy);
      }
      else {
        this.project.team_members = [...JSON.parse(team_member_copy)];
      }
      this.ChangeClientsOrTeamMembers();
    });
    return await modal.present()

  }


  async presentSelectGroupsModal() {
    const modal = await this.modalController.create({
      component: GroupsManagerPage,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'projects',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...this.project.groups],
        modal_title: 'Select Groups'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      // A group may have been created in the modal - reload the list the group search box works from
      this.GetAllGroups();
      if (data && data.data) {
        this.project.groups = data.data;
      }
    });
    return await modal.present();
  }



  async RemoveTeamMember(tm) {
    this.globalService.RemoveFromArray(this.project.team_members, tm.guid);
    this.ChangeClientsOrTeamMembers();

  }

  // After a template has been saved: find the repeating schedules that were created from it and offer to bring them up to date,
  // the same way the schedule editor's "update all related" does. Each schedule's copy of the template gets the template's new
  // tasks and actions, and its upcoming projects that haven't been changed are rebuilt from it. Projects that have been worked
  // on, and ones already in the past, are left alone. (One-off projects keep no link to their template, so aren't included.)
  private async OfferUpdateProjectsFromTemplate() {
    // Only offered to someone who can see and update the schedules created from a template
    if (!this.globalService.Can('schedule_templates_read') || !this.globalService.Can('schedule_templates_update')) return;
    await this.globalService.presentLoading('Checking for projects created from this template...', 99999999);
    const related: any = await this.apiService.get(`templates/projects/${this.project.template_guid}/related`);
    await this.globalService.dismissLoading();
    if (!related?.success || !Array.isArray(related.data) || related.data.length === 0) return;
    const schedules: any[] = related.data;

    // A dialog lists every schedule and its upcoming projects (who each is for, which will be rebuilt and which have been
    // changed / worked on and will be left alone) and lets the accountant untick any schedule to leave out
    const dialog = await this.modalController.create({
      component: UpdateRelatedProjectsComponent,
      cssClass: 'large-modal tall-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        schedules,
        // Match the backend's priority exactly (ProjectTemplateRelated.cs UpdateRelated): Project Title first,
        // Template Name only as a fallback for the rare template that somehow has no title.
        template_title: this.project.title || this.project.template_name || ''
      }
    });
    await dialog.present();
    const { data: choice } = await dialog.onDidDismiss();
    const chosen: any[] = schedules.filter(s => (choice?.schedule_guids || []).includes(s.schedule_guid));
    if (chosen.length === 0) return;

    // One request per schedule, so a long list can't time out and progress can be shown
    let failed = 0;
    for (let i = 0; i < chosen.length; i++) {
      await this.globalService.presentLoading(`Updating schedule ${i + 1} of ${chosen.length}...`, 99999999);
      const result: any = await this.apiService.put(`templates/projects/${this.project.template_guid}/related/${chosen[i].schedule_guid}`,
        { update_project_title: !!choice?.update_titles });
      if (!result?.success) failed++;
    }
    await this.globalService.dismissLoading();
    this.globalService.presentToastMessage(
      failed === 0
        ? `Updated ${chosen.length} schedule${chosen.length === 1 ? '' : 's'}.`
        : `${chosen.length - failed} of ${chosen.length} schedules updated. ${failed} failed - please try again.`,
      failed === 0
    );
  }

  async UpdateTemplate() {
    if (!this.edit_schedule_mode) {
      const data: any = await this.apiService.put(`templates/projects/${this.project.template_guid}`, this.project);
      this.globalService.presentToastMessage(data.message, data.success);
      if (data.success) {
        await this.OfferUpdateProjectsFromTemplate();
        this.modalController.dismiss(true);
      }
    } else if (this.edit_all_related && this.related_schedules.length > 0) {
      await this.UpdateAllRelatedSchedules();
    } else {
      await this.UpdateCurrentScheduleTemplate();
    }
  }


  //Functions to do verification that the inputs are all valid.
  ProjectSchedulecheck() {
    let error_message = '';

    if (dayjs(this.project.internal_end_date).isBefore(dayjs(this.project.internal_start_date))) {
      error_message += 'Project end date must be after project start date.<br>';
    }
    if (dayjs(this.project.external_end_date).isBefore(dayjs(this.project.external_start_date))) {
      error_message += 'External end date must be after external start date.<br>';
    }
    if (dayjs(this.project.external_start_date).isBefore(dayjs(this.project.internal_start_date))) {
      error_message += 'External start date must be after project start date.<br>';
    }
    if (dayjs(this.project.external_end_date).isAfter(dayjs(this.project.internal_end_date))) {
      error_message += 'External end date must be before project end date.<br>';
    }

    // if (this.FixStartDate(false) != this.project.internal_start_date) {
    //   error_message += 'Project Repeat Start Date is invalid.<br>';
    // }
    if (this.project.repeat_rules.end_type == 'date') {
      if (this.project.repeat_rules.end_date == null) {
        error_message += 'End date is required when using "date" as the end type.<br>';
      } else if (dayjs(this.project.repeat_rules.end_date).isBefore(dayjs(this.project.internal_start_date))) {
        error_message += 'End date must be after the project start date.<br>';
      }
    }
    if (this.project.repeat_rules.frequency == 'semi-monthly') {
      if (this.project.repeat_rules.semi_monthly_second_day <= this.project.repeat_rules.semi_monthly_first_day) error_message += "The second day in semi monthly must be after the first day."
    }
    if (this.project.repeat_rules.frequency === 'weekly') {
      const hasWeekday = Array.isArray(this.project.repeat_rules.weekdays) && this.project.repeat_rules.weekdays.some((w: boolean) => w);
      if (!hasWeekday) error_message += 'Please select at least one weekday for the weekly repeat schedule.<br>';
    }
    if (this.project.repeat_rules.custom_start_date && this.projectsService.FixStartDate(false, this.project.repeat_rules)) {
      error_message += "The project repeat start date is invalid. Please click Fix Date to fix.";
    }

    // Check for invalid offset_type combinations when frequency is monthly
    if (this.project.repeat_rules.frequency === 'monthly' || this.project.repeat_rules.frequency == 'quarterly' || this.project.repeat_rules.frequency == 'semi-annually' || this.project.repeat_rules.frequency == 'yearly') {
      if (this.project.repeat_rules.internal_start_offset_unit !== 'month' &&
        (this.project.repeat_rules.internal_start_offset_type === 'start_of_month' ||
          this.project.repeat_rules.internal_start_offset_type === 'end_of_month')) {
        error_message += '"Start of Month" and "End of Month" can only be used when the Project Start offset unit is set to "Month(s)".<br>';
      }

      if (this.project.repeat_rules.internal_end_offset_unit !== 'month' &&
        (this.project.repeat_rules.internal_end_offset_type === 'start_of_month' ||
          this.project.repeat_rules.internal_end_offset_type === 'end_of_month')) {
        error_message += '"Start of Month" and "End of Month" can only be used when the Project End offset unit is set to "Month(s)".<br>';
      }

      if (this.project.repeat_rules.external_start_offset_unit !== 'month' &&
        (this.project.repeat_rules.external_start_offset_type === 'start_of_month' ||
          this.project.repeat_rules.external_start_offset_type === 'end_of_month')) {
        error_message += '"Start of Month" and "End of Month" can only be used when the Client Start offset unit is set to "Month(s)".<br>';
      }

      if (this.project.repeat_rules.external_end_offset_unit !== 'month' &&
        (this.project.repeat_rules.external_end_offset_type === 'start_of_month' ||
          this.project.repeat_rules.external_end_offset_type === 'end_of_month')) {
        error_message += '"Start of Month" and "End of Month" can only be used when the Client End offset unit is set to "Month(s)".<br>';
      }

      if (this.project.repeat_rules.period_start_offset_unit !== 'month' &&
        (this.project.repeat_rules.period_start_offset_type === 'start_of_month' ||
          this.project.repeat_rules.period_start_offset_type === 'end_of_month')) {
        error_message += '"Start of Month" and "End of Month" can only be used when the Period Start offset unit is set to "Month(s)".<br>';
      }

      if (this.project.repeat_rules.period_end_offset_unit !== 'month' &&
        (this.project.repeat_rules.period_end_offset_type === 'start_of_month' ||
          this.project.repeat_rules.period_end_offset_type === 'end_of_month')) {
        error_message += '"Start of Month" and "End of Month" can only be used when the Period End offset unit is set to "Month(s)".<br>';
      }
    }

    //We need to make sure the dates make sense

    return error_message;

  }





  ProjectDetailsCheck() {
    let error_message = '';
    if (this.project.title == '') error_message += 'Project must have a title<br>';
    if (!this.project.create_template && !this.edit_template_mode && (this.project.team_leader == '' || !this.project.team_leader)) error_message += 'Project Team leader is required.<br>';
    if (this.project.create_template && this.project.template_name == '') {
      error_message += 'Template name is required when creating a template.<br>';
    }
    if (!this.project.create_template && (this.project.assigned_companies.length + this.project.assigned_clients.length) == 0 && !this.edit_template_mode) error_message += 'You must assign at least one contact or company to this project.<br>';

    if (!this.project.create_template && !this.edit_template_mode && !this.project.all_team_members && this.project.team_members.length == 0) {
      error_message += 'Please assign at least one team member to the project, or select "All Team Members".<br>';
    }

    if (!this.team_members_valid) {
      error_message += 'One or more selected team members do not have permission to access the selected clients.<br>';
    }

    return error_message;

  }

  ProjectTasksCheck() {
    let error_message = '';
    if (this.project.tasks.length == 0) error_message += 'You must add at least one task to the project.<br>';
    if (!this.project.create_template && this.skills_warning && !this.skills_warning_ignored) {
      error_message += 'One or more tasks have no assigned team member with the required skills. Resolve the conflict or use "Ignore Skills Conflict and Create Project".<br>';
    }
    if (this.project.assignment_settings === 2 && this.project.tasks.some((t: any) => !t.assigned_member_guid)) {
      error_message += 'All tasks must have a team member assigned. Use the Assignment Type column to assign each task.<br>';
    }
    const no_access_tasks = this.project.tasks.filter((t: any) => this.TaskAssigneeLacksAccess(t));
    if (no_access_tasks.length > 0) {
      error_message += `These tasks are assigned to a team member who can't access every contact/company on this project, so they wouldn't be able to see it: ${no_access_tasks.map((t: any) => `"${t.title}"`).join(', ')}. Pick someone else in the Assignment Type column.<br>`;
    }
    if (this.project.is_internal && this.projectsService.ProjectHasExternalSteps(this.project)) {
      error_message += 'The project is marked as internal, but it contains tasks with actions that require client access. Please remove those actions or mark the project as external.\n';
    }
    if (this.created_with_ai) {

      if (this.project.tasks.some(task => !task.checked_details)) {
        error_message += 'This project was created with AI. Please review all tasks and actions to ensure they are correct and make any necessary adjustments before saving.\n';
      }

    }

    if (!this.project.create_template && !this.edit_template_mode) {
      const companies: any[] = this.project.assigned_companies || [];
      if (companies.length > 0) {
        for (const task of this.project.tasks) {
          for (const step of (task.steps || [])) {
            if (!ClientStepTypes.includes(Number(step.type))) continue;
            if (!step.data?.send_to_different_role || !step.data?.override_role_guid) continue;
            const overrideRoleGuid = step.data.override_role_guid;
            const role = this.clients_roles.find((r: any) => r.guid === overrideRoleGuid);
            const roleName = role ? role.title : 'the selected role';
            for (const company of companies) {
              const clients: any[] = company.clients || [];
              const hasDefault = clients.some((c: any) =>
                c.role_guid === overrideRoleGuid && c.is_default_for_role
              );
              if (!hasDefault) {
                error_message += `"${company.name}" has no default contact for the "${roleName}" role, required by action "${step.title}" in task "${task.title}".<br>`;
              }
            }
          }
        }
      }
    }

    return error_message;

  }


  get exampleRepeatStartDate(): string {
    const r = this.project?.repeat_rules;
    if (!r || !this.project?.repeat) return '';
    if (this.project.create_template) {
      if (r.relative_date_type === 'year_end_date') {
        const yed = dayjs(this.template_example_date);
        console.log("Year End Date String:", this.template_example_date);
        return dayjs().year(dayjs().year()).month(yed.month()).date(yed.date()).format('MMMM D, YYYY');
      }
      // Specified Start Date: always reflect the next valid date for the current rules, same as a real project.
      const next = this.projectsService.GetNextValidDateFromToday(r);
      return dayjs(next).format('MMMM D, YYYY');
    }
    if (r.relative_date_type === 'year_end_date') {
      // The schedule preview already walks the year-end date back far enough to respect
      // "Repeat Projects Starting" (including multi-year intervals) - reuse its first
      // occurrence so this badge doesn't drift out of sync with what the preview shows.
      const preview = this.previewFirstOccurrence;
      if (preview?.occurrence_date) {
        return dayjs(preview.occurrence_date).format('MMMM D, YYYY');
      }
      const previewClient = this.schedulePreviewRef?.selectedPreviewClient;
      const yearEndStr = previewClient?.year_end_date
        ?? this.project.assigned_clients?.[0]?.year_end_date
        ?? this.project.assigned_companies?.[0]?.year_end_date;
      console.log("Year End Date String:", yearEndStr);
      if (yearEndStr) {
        const yed = dayjs(yearEndStr);
        return dayjs().year(dayjs().year()).month(yed.month()).date(yed.date()).format('MMMM D, YYYY');
      }
      return '';
    }
    if (r.custom_start_date) {
      const fixed = this.projectsService.FixStartDate(false, r) ?? r.repeat_start_date;
      return dayjs(fixed).format('MMMM D, YYYY');
    }
    const next = this.projectsService.GetNextValidDateFromToday(r);
    return dayjs(next).format('MMMM D, YYYY');
  }


  onExampleDateChange(date: string) {
    if (!date) return;
    "2026-10-31T00:00:00"
    let parsedDate = dayjs(date).format("YYYY-MM-DDT00:00:00");
    console.log("Parsed Date:", parsedDate);
    this.project.assigned_clients[0].year_end_date = parsedDate;
    this.template_example_date = date;
    this.project.repeat_rules.repeat_start_date = date;
    this.onUpdateDates(this.project);
    this.editing_example_date = false;
  }

  get allowedOffsetUnits(): string[] {
    const freq = this.project?.repeat_rules?.frequency;
    if (freq === 'daily') return ['day'];
    if (freq === 'weekly') return ['day', 'week'];
    return ['day', 'week', 'month'];
  }

  onUpdateDates(inc_project) {
    // For templates, anchor all date calculations to the example date. When repeating from a
    // specific start date (not a client year end), that example always tracks the next valid
    // date for the current rules, the same as a real (non-template) project would.
    if (inc_project.create_template) {
      if (inc_project.repeat_rules.relative_date_type === 'year_end_date') {
        inc_project.repeat_rules.repeat_start_date = this.template_example_date;
      } else {
        inc_project.repeat_rules.repeat_start_date = this.projectsService.GetNextValidDateFromToday(inc_project.repeat_rules);
      }
    }

    // Clamp offset units to what's valid for the current frequency
    const allowed = this.allowedOffsetUnits;
    const offsetPrefixes = ['internal_start', 'internal_end', 'external_start', 'external_end', 'period_start', 'period_end'];
    for (const prefix of offsetPrefixes) {
      const unitKey = `${prefix}_offset_unit`;
      if (inc_project.repeat_rules[unitKey] && !allowed.includes(inc_project.repeat_rules[unitKey])) {
        inc_project.repeat_rules[unitKey] = 'day';
      }
    }

    if (inc_project.sync_client_dates) {



      inc_project.repeat_rules.external_start_offset = inc_project.repeat_rules.internal_start_offset;
      inc_project.repeat_rules.external_start_offset_unit = inc_project.repeat_rules.internal_start_offset_unit;
      inc_project.repeat_rules.external_start_offset_type = inc_project.repeat_rules.internal_start_offset_type;
      inc_project.repeat_rules.external_start_date_before = inc_project.repeat_rules.internal_start_date_before;

      inc_project.repeat_rules.external_end_offset = inc_project.repeat_rules.internal_end_offset;
      inc_project.repeat_rules.external_end_offset_unit = inc_project.repeat_rules.internal_end_offset_unit;
      inc_project.repeat_rules.external_end_offset_type = inc_project.repeat_rules.internal_end_offset_type;
      inc_project.repeat_rules.external_end_date_before = inc_project.repeat_rules.internal_end_date_before;
    }

    if (!inc_project.repeat) {
      this.SyncClientDatesToProjectDates(inc_project);
      return;
    }
    // Calculate project dates based on repeat start date and offsets
    let internal_start_mod = inc_project.repeat_rules.internal_start_date_before ? -1 : 1;

    let internal_end_mod = inc_project.repeat_rules.internal_end_date_before ? -1 : 1;
    let external_start_mod = inc_project.repeat_rules.external_start_date_before ? -1 : 1;
    let external_end_mod = inc_project.repeat_rules.external_end_date_before ? -1 : 1;
    let period_start_mod = inc_project.repeat_rules.period_start_date_before ? -1 : 1;
    let period_end_mod = inc_project.repeat_rules.period_end_date_before ? -1 : 1;

    inc_project.internal_start_date = dayjs(inc_project.repeat_rules.repeat_start_date)
      .add(inc_project.repeat_rules.internal_start_offset * internal_start_mod, inc_project.repeat_rules.internal_start_offset_unit)
      .format("YYYY-MM-DD");

    inc_project.internal_end_date = dayjs(inc_project.repeat_rules.repeat_start_date)
      .add(inc_project.repeat_rules.internal_end_offset * internal_end_mod, inc_project.repeat_rules.internal_end_offset_unit)
      .format("YYYY-MM-DD");

    inc_project.external_start_date = dayjs(inc_project.repeat_rules.repeat_start_date)
      .add(inc_project.repeat_rules.external_start_offset * external_start_mod, inc_project.repeat_rules.external_start_offset_unit)
      .format("YYYY-MM-DD");

    inc_project.external_end_date = dayjs(inc_project.repeat_rules.repeat_start_date)
      .add(inc_project.repeat_rules.external_end_offset * external_end_mod, inc_project.repeat_rules.external_end_offset_unit)
      .format("YYYY-MM-DD");

    // Set period_start_date and period_end_date using the same logic
    inc_project.period_start_date = dayjs(inc_project.repeat_rules.repeat_start_date)
      .add(inc_project.repeat_rules.period_start_offset * period_start_mod, inc_project.repeat_rules.period_start_offset_unit)
      .format("YYYY-MM-DD");

    inc_project.period_end_date = dayjs(inc_project.repeat_rules.repeat_start_date)
      .add(inc_project.repeat_rules.period_end_offset * period_end_mod, inc_project.repeat_rules.period_end_offset_unit)
      .format("YYYY-MM-DD");




    if (inc_project.repeat_rules.frequency === 'monthly' || inc_project.repeat_rules.frequency == 'quarterly' || inc_project.repeat_rules.frequency == 'semi-annually' || inc_project.repeat_rules.frequency == 'yearly') {

      if (inc_project.repeat_rules.internal_start_offset_type === 'start_of_month') {
        inc_project.internal_start_date = dayjs(inc_project.internal_start_date).startOf('month').format("YYYY-MM-DD");
      }
      else if (inc_project.repeat_rules.internal_start_offset_type === 'end_of_month') {
        inc_project.internal_start_date = dayjs(inc_project.internal_start_date).endOf('month').format("YYYY-MM-DD");
      }
      if (inc_project.repeat_rules.internal_end_offset_type === 'start_of_month') {
        inc_project.internal_end_date = dayjs(inc_project.internal_end_date).startOf('month').format("YYYY-MM-DD");
      }
      else if (inc_project.repeat_rules.internal_end_offset_type === 'end_of_month') {
        inc_project.internal_end_date = dayjs(inc_project.internal_end_date).endOf('month').format("YYYY-MM-DD");
      }

      if (inc_project.repeat_rules.external_start_offset_type === 'start_of_month') {
        inc_project.external_start_date = dayjs(inc_project.external_start_date).startOf('month').format("YYYY-MM-DD");
      }
      else if (inc_project.repeat_rules.external_start_offset_type === 'end_of_month') {
        inc_project.external_start_date = dayjs(inc_project.external_start_date).endOf('month').format("YYYY-MM-DD");
      }

      if (inc_project.repeat_rules.external_end_offset_type === 'start_of_month') {
        inc_project.external_end_date = dayjs(inc_project.external_end_date).startOf('month').format("YYYY-MM-DD");
      }
      else if (inc_project.repeat_rules.external_end_offset_type === 'end_of_month') {
        inc_project.external_end_date = dayjs(inc_project.external_end_date).endOf('month').format("YYYY-MM-DD");
      }

      if (inc_project.repeat_rules.period_start_offset_type === 'start_of_month') {
        inc_project.period_start_date = dayjs(inc_project.period_start_date).startOf('month').format("YYYY-MM-DD");
      }
      else if (inc_project.repeat_rules.period_start_offset_type === 'end_of_month') {
        inc_project.period_start_date = dayjs(inc_project.period_start_date).endOf('month').format("YYYY-MM-DD");
      }

      if (inc_project.repeat_rules.period_end_offset_type === 'start_of_month') {
        inc_project.period_end_date = dayjs(inc_project.period_end_date).startOf('month').format("YYYY-MM-DD");
      }
      else if (inc_project.repeat_rules.period_end_offset_type === 'end_of_month') {
        inc_project.period_end_date = dayjs(inc_project.period_end_date).endOf('month').format("YYYY-MM-DD");
      }
    } else {
      inc_project.repeat_rules.internal_start_offset_type = 'calculated_date';
      inc_project.repeat_rules.internal_end_offset_type = 'calculated_date';
      inc_project.repeat_rules.external_start_offset_type = 'calculated_date';
      inc_project.repeat_rules.external_end_offset_type = 'calculated_date';
      inc_project.repeat_rules.period_start_offset_type = 'calculated_date';
      inc_project.repeat_rules.period_end_offset_type = 'calculated_date';
    }

    if (this.project.repeat) {
      this.RefreshClientSchedulePreview();
    }
  }

  RefreshClientSchedulePreview() {
    this.schedulePreviewRef?.Refresh();
  }

  get previewFirstOccurrence(): any {
    return this.schedulePreviewRef?.clientSchedulePreview?.[0] ?? null;
  }

  get previewClientName(): string | null {
    const c = this.schedulePreviewRef?.selectedPreviewClient;
    if (!c) return null;
    return this.schedulePreviewRef.selectedPreviewIsCompany ? c.name : c.display_name;
  }

  getDisplayNameByGuid(guid) {
    const member = this.team_members.find(tm => tm.guid === guid);
    return member ? member.display_name : '';
  }

  getRepeatRulesDescription(): string {
    const r: RepeatRules = this.project?.repeat_rules;
    if (!r || !r.frequency) return 'No repeat schedule configured.';

    const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const ORDINAL_LABELS: Record<string, string> = { '1': '1st', '2': '2nd', '3': '3rd', '4': '4th', '5': 'last', 'last': 'last' };

    const interval = Math.max(1, r.interval || 1);
    let parts: string[] = [];

    // --- Frequency phrase ---
    switch (r.frequency) {
      case 'daily':
        parts.push(interval === 1 ? 'Every day' : `Every ${interval} days`);
        break;

      case 'weekly': {
        const selectedDays = (r.weekdays || [])
          .map((on, i) => on ? WEEKDAY_NAMES[i] : null)
          .filter(Boolean);
        const dayStr = selectedDays.length ? selectedDays.join(', ') : 'any day';
        parts.push(interval === 1 ? `Every week on ${dayStr}` : `Every ${interval} weeks on ${dayStr}`);
        break;
      }

      case 'monthly': {
        const prefix = interval === 1 ? 'Every month' : `Every ${interval} months`;
        parts.push(prefix + this._monthlyDayPhrase(r, ORDINAL_LABELS, WEEKDAY_NAMES));
        if (!r.monthly_any && r.months?.some(Boolean)) {
          const allowed = MONTH_NAMES.filter((_, i) => r.months[i]);
          parts.push(`in ${allowed.join(', ')}`);
        }
        break;
      }

      case 'quarterly':
        parts.push('Every quarter' + this._monthlyDayPhrase(r, ORDINAL_LABELS, WEEKDAY_NAMES));
        break;

      case 'semi-annually':
        parts.push('Twice a year' + this._monthlyDayPhrase(r, ORDINAL_LABELS, WEEKDAY_NAMES));
        break;

      case 'semi-monthly':
        parts.push(`Twice a month on the ${r.semi_monthly_first_day}${this._ordSuffix(r.semi_monthly_first_day)} and ${r.semi_monthly_second_day}${this._ordSuffix(r.semi_monthly_second_day)}`);
        break;

      case 'twice-monthly':
        parts.push('Twice a month');
        break;

      case 'yearly': {
        const prefix = interval === 1 ? 'Every year' : `Every ${interval} years`;
        if (r.yearly_use_ordinal) {
          const ord = ORDINAL_LABELS[r.yearly_ordinal] || r.yearly_ordinal;
          const wd = WEEKDAY_NAMES[Number(r.yearly_weekday)] || '';
          const mo = MONTH_NAMES[(r.yearly_ordinal_month || 1) - 1] || '';
          parts.push(`${prefix} on the ${ord} ${wd} of ${mo}`);
        } else {
          const mo = MONTH_NAMES[(r.yearly_month || 1) - 1] || '';
          parts.push(`${prefix} on ${mo} ${r.yearly_day}${this._ordSuffix(r.yearly_day)}`);
        }
        break;
      }

      default:
        parts.push(`Repeats ${r.frequency}`);
    }

    // --- End condition ---
    if (r.end_type === 'on' && r.end_date) {
      parts.push(`· Ends ${dayjs(r.end_date).format('MMM D, YYYY')}`);
    } else if (r.end_type === 'after' && r.end_after) {
      parts.push(`· Ends after ${r.end_after} occurrence${r.end_after === 1 ? '' : 's'}`);
    } else {
      parts.push('· No end date');
    }

    // --- Date offset rules ---
    const isMonthly = ['monthly', 'quarterly', 'semi-annually', 'yearly'].includes(r.frequency);
    const p = this.project;
    const dateRows: { label: string; offset: number; unit: string; before: boolean; offsetType?: string; firstDate: string }[] = [
      { label: 'Project Start', offset: r.internal_start_offset, unit: r.internal_start_offset_unit || 'day', before: !!r.internal_start_date_before, offsetType: isMonthly ? r.internal_start_offset_type : null, firstDate: p.internal_start_date },
      { label: 'Project End', offset: r.internal_end_offset, unit: r.internal_end_offset_unit || 'day', before: !!r.internal_end_date_before, offsetType: isMonthly ? r.internal_end_offset_type : null, firstDate: p.internal_end_date },
      // { label: 'Client Start', offset: r.external_start_offset, unit: r.external_start_offset_unit || 'day', before: !!r.external_start_date_before, offsetType: isMonthly ? r.external_start_offset_type : null, firstDate: p.external_start_date },
      // { label: 'Client End', offset: r.external_end_offset, unit: r.external_end_offset_unit || 'day', before: !!r.external_end_date_before, offsetType: isMonthly ? r.external_end_offset_type : null, firstDate: p.external_end_date },
      { label: 'Period Start', offset: r.period_start_offset, unit: r.period_start_offset_unit || 'day', before: !!r.period_start_date_before, offsetType: isMonthly ? r.period_start_offset_type : null, firstDate: p.period_start_date },
      { label: 'Period End', offset: r.period_end_offset, unit: r.period_end_offset_unit || 'day', before: !!r.period_end_date_before, offsetType: isMonthly ? r.period_end_offset_type : null, firstDate: p.period_end_date },
    ];

    const labelWidth = 14;
    const dateLines = dateRows
      .filter(row => row.firstDate)
      .map(row => {
        const lbl = (row.label + ':').padEnd(labelWidth);
        const phrase = this._offsetPhrase(row.offset, row.unit, row.before, row.offsetType);
        const preview = row.firstDate ? `→ ${dayjs(row.firstDate).format('MMM D, YYYY')}` : '';
        return `  ${lbl} ${phrase}  ${preview}`;
      });

    if (dateLines.length) {
      parts.push('\n\nDate Rules (per occurrence):\n' + dateLines.join('\n'));
    }

    return parts.join(' ');
  }

  getDateOffsetPhrase(offset: number, unit: string, before: boolean, offsetType?: string): string {
    return this._offsetPhrase(offset, unit, before, offsetType);
  }

  getRelativeDateAnchorLabel(): string {
    const type = this.project?.repeat_rules?.relative_date_type;
    if (type === 'year_end_date') return "client's fiscal year end";
    if (type === 'internal_start_date') return 'project start date';
    return 'repeat date';
  }

  private _offsetPhrase(offset: number, unit: string, before: boolean, offsetType?: string): string {
    const n = Number(offset) || 0;
    const unitLabel = unit === 'day' ? (n === 1 ? 'day' : 'days')
      : unit === 'week' ? (n === 1 ? 'week' : 'weeks')
        : (n === 1 ? 'month' : 'months');
    let phrase = n === 0 ? 'same as repeat date' : `${n} ${unitLabel} ${before ? 'before' : 'after'} the repeat date`;
    if (offsetType === 'start_of_month') phrase += ', snapped to start of month';
    else if (offsetType === 'end_of_month') phrase += ', snapped to end of month';
    return phrase;
  }

  private _monthlyDayPhrase(r: RepeatRules, ordLabels: Record<string, string>, weekdayNames: string[]): string {
    if (r.monthly_type === 'day' && r.monthly_day) {
      return ` on the ${r.monthly_day}${this._ordSuffix(r.monthly_day)}`;
    }
    if (r.monthly_type === 'weekday') {
      const ord = ordLabels[r.monthly_ordinal] || r.monthly_ordinal;
      const wd = weekdayNames[Number(r.monthly_weekday)] || '';
      return ` on the ${ord} ${wd}`;
    }
    if (r.monthly_type === 'special') {
      if (r.monthly_special === 'start') return ' at the start of the month';
      if (r.monthly_special === 'end') return ' at the end of the month';
    }
    return '';
  }

  private _ordSuffix(n: number): string {
    if (!n) return '';
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  async Log() {
    console.log("Project:", this.project);
  }

  GetProjectRoleName(role) {
    if (role == 1) return "Leader";
    else if (role == 2) return "Worker";
    else if (role == 3) return "Worker (Alt)";
    else if (role == 4) return "Follower";
  }

  // Checks that every team member who'll have access to this project (explicit list, or the team
  // leader) is actually allowed to see every assigned client/company. Previously this made one
  // sequential HTTP round trip per (team member x client/company) pair - for a firm with e.g. 20 team
  // members and 30 assigned clients that's 600 awaited requests, one at a time, which is what made the
  // page feel unresponsive while creating a project. Now it fetches each team member's *entire* allowed
  // client/company list in one pair of requests (reusing the same "permission_check=1" bulk endpoint the
  // old >50 shortcut already relied on) and fires all team members' requests in parallel via Promise.all,
  // so the total is `2 x team members` requests running concurrently instead of `members x entities`
  // sequentially.
  //
  // When there's a small, known set of entities to check (under 30), pass their GUIDs so the backend
  // can narrow the scan to just those rows instead of pulling every client/company in the firm.
  private BuildPermissionCheckGuidsParam(entities: any[]): string {
    if (!entities || entities.length === 0 || entities.length >= 30) return '';
    return `&guids=${entities.map((e: any) => e.guid).join(',')}`;
  }

  // Team members who can be given a specific task in the current tasks list. Every assigned client/company
  // gets its own copy of the project, so a member must be able to access all of them - otherwise they can't
  // see the project or the task. null until worked out (everyone is offered in the meantime).
  task_assignable_member_guids: Set<string> | null = null;
  private task_assignable_generation = 0;

  async RefreshTaskAssignableMembers() {
    const generation = ++this.task_assignable_generation;
    const per_entity = await this.ResolveTeamMembersPerAssignedEntity();
    if (generation !== this.task_assignable_generation) return; // a newer refresh has started
    let allowed: any[] = per_entity[0]?.members || [];
    for (const entity of per_entity.slice(1)) {
      const entity_guids = new Set((entity.members || []).map((m: any) => m.guid));
      allowed = allowed.filter((m: any) => entity_guids.has(m.guid));
    }
    this.task_assignable_member_guids = new Set(allowed.map((m: any) => m.guid));
  }

  CanBeAssignedTask(member: any): boolean {
    return !this.task_assignable_member_guids || this.task_assignable_member_guids.has(member?.guid);
  }

  TaskAssigneeLacksAccess(task: any): boolean {
    return !!task?.assigned_member_guid && !this.CanBeAssignedTask({ guid: task.assigned_member_guid });
  }

  async ChangeClientsOrTeamMembers() {
    void this.RefreshTaskAssignableMembers();

    // Reset errors
    for (const company of this.project.assigned_companies) {
      company.permission_error = '';
    }
    for (const client of this.project.assigned_clients) {
      client.permission_error = '';
    }

    let membersToCheck: any = [];
    // Shortcut: all team members allowed
    if (this.project.all_team_members) {
      membersToCheck = [];
    }
    else {
      membersToCheck = [...this.project.team_members];
    }

    // Add project team leader to the members to check if one is selected
    if (this.project.team_leader) {
      const teamLeaderGuid = typeof this.project.team_leader === 'string'
        ? this.project.team_leader
        : this.project.team_leader.guid;

      const teamLeaderMember = this.team_members.find(tm => tm.guid === teamLeaderGuid);
      if (teamLeaderMember && !membersToCheck.find(m => m.guid === teamLeaderGuid)) {
        membersToCheck.push(teamLeaderMember);
      }
    }

    const clientsToCheck = this.project.assigned_clients.filter((c: any) => !c.all_team_members);
    const companiesToCheck = this.project.assigned_companies.filter((c: any) => !c.all_team_members);
    const nonAdminMembers: any[] = membersToCheck.filter((tm: any) => tm.is_admin != 1);

    if ((clientsToCheck.length === 0 && companiesToCheck.length === 0) || nonAdminMembers.length === 0) {
      this.team_members_valid = true;
      return;
    }

    let team_members_valid = true;
    this.checking_permissions = true;

    const client_guids_param = this.BuildPermissionCheckGuidsParam(clientsToCheck);
    const company_guids_param = this.BuildPermissionCheckGuidsParam(companiesToCheck);

    let results: { teamMember: any; client_guids: Set<string>; company_guids: Set<string> }[];
    try {
      results = await Promise.all(
        nonAdminMembers.map(async (teamMember: any) => {
          const [client_data, company_data]: any[] = await Promise.all([
            clientsToCheck.length > 0
              ? this.apiService.get(`clients?team_member=${teamMember.guid}&permission_check=1${client_guids_param}`)
              : Promise.resolve(null),
            companiesToCheck.length > 0
              ? this.apiService.get(`companies?team_member=${teamMember.guid}&permission_check=1${company_guids_param}`)
              : Promise.resolve(null)
          ]);
          return {
            teamMember,
            client_guids: new Set<string>((client_data?.data || []).map((c: any) => c.guid)),
            company_guids: new Set<string>((company_data?.data || []).map((c: any) => c.guid))
          };
        })
      );
    } finally {
      this.checking_permissions = false;
    }

    for (const { teamMember, client_guids, company_guids } of results) {
      for (const client of clientsToCheck) {
        if (!client_guids.has(client.guid)) {
          team_members_valid = false;
          client.permission_error =
            (client.permission_error || '') +
            (client.permission_error ? ', ' : '') +
            teamMember.display_name;
        }
      }
      for (const company of companiesToCheck) {
        if (!company_guids.has(company.guid)) {
          team_members_valid = false;
          company.permission_error =
            (company.permission_error || '') +
            (company.permission_error ? ', ' : '') +
            teamMember.display_name;
        }
      }
    }

    this.team_members_valid = team_members_valid;
    this.CheckTaskSkillCoverage();
  }

  // The explicit (non "all team members") assigned pool: the team + leader that ChangeClientsOrTeamMembers
  // already validates has access to every assigned client/company, so it's uniform across all of them.
  private ResolveExplicitTeamPool(): any[] {
    const pool = [...this.project.team_members];
    if (this.project.team_leader) {
      const teamLeaderGuid = typeof this.project.team_leader === 'string'
        ? this.project.team_leader
        : this.project.team_leader.guid;
      const teamLeaderMember = this.team_members.find(tm => tm.guid === teamLeaderGuid);
      if (teamLeaderMember && !pool.find(m => m.guid === teamLeaderGuid)) pool.push(teamLeaderMember);
    }
    return pool;
  }

  // Each assigned client/company gets its own separate project instance, so "who could work on this
  // project" isn't one shared pool when using "All Team Members" - a member with access to only one of
  // ten assigned clients doesn't help the other nine. Resolves the actual candidate pool per entity so
  // skill coverage can be checked against each one individually instead of a misleading combined pool.
  async ResolveTeamMembersPerAssignedEntity(): Promise<{ name: string; members: any[] }[]> {
    const assignedClients: any[] = this.project.assigned_clients || [];
    const assignedCompanies: any[] = this.project.assigned_companies || [];

    if (assignedClients.length === 0 && assignedCompanies.length === 0) {
      // Nothing assigned yet - can't narrow down by entity, just check against the whole candidate pool.
      const pool = this.project.all_team_members ? this.team_members : this.ResolveExplicitTeamPool();
      return [{ name: 'this project', members: pool }];
    }

    if (!this.project.all_team_members) {
      // Fixed team - the same explicit pool applies to every entity (already validated elsewhere to
      // have access to all of them), so no per-entity permission lookups are needed.
      const pool = this.ResolveExplicitTeamPool();
      return [
        ...assignedClients.map((c: any) => ({ name: c.display_name, members: pool })),
        ...assignedCompanies.map((c: any) => ({ name: c.name, members: pool }))
      ];
    }

    // "All Team Members": entities open to everyone don't need a lookup; the rest need per-entity access.
    const openClients = assignedClients.filter((c: any) => c.all_team_members);
    const openCompanies = assignedCompanies.filter((c: any) => c.all_team_members);
    const restrictedClients = assignedClients.filter((c: any) => !c.all_team_members);
    const restrictedCompanies = assignedCompanies.filter((c: any) => !c.all_team_members);

    const result: { name: string; members: any[] }[] = [
      ...openClients.map((c: any) => ({ name: c.display_name, members: this.team_members })),
      ...openCompanies.map((c: any) => ({ name: c.name, members: this.team_members }))
    ];

    if (restrictedClients.length === 0 && restrictedCompanies.length === 0) return result;

    const adminMembers = this.team_members.filter((tm: any) => tm.is_admin == 1);
    const nonAdminMembers = this.team_members.filter((tm: any) => tm.is_admin != 1);

    const client_guids_param = this.BuildPermissionCheckGuidsParam(restrictedClients);
    const company_guids_param = this.BuildPermissionCheckGuidsParam(restrictedCompanies);

    const perMemberAccess: { teamMember: any; client_guids: Set<string>; company_guids: Set<string> }[] = await Promise.all(
      nonAdminMembers.map(async (teamMember: any) => {
        const [client_data, company_data]: any[] = await Promise.all([
          restrictedClients.length > 0
            ? this.apiService.get(`clients?team_member=${teamMember.guid}&permission_check=1${client_guids_param}`)
            : Promise.resolve(null),
          restrictedCompanies.length > 0
            ? this.apiService.get(`companies?team_member=${teamMember.guid}&permission_check=1${company_guids_param}`)
            : Promise.resolve(null)
        ]);
        return {
          teamMember,
          client_guids: new Set<string>((client_data?.data || []).map((c: any) => c.guid)),
          company_guids: new Set<string>((company_data?.data || []).map((c: any) => c.guid))
        };
      })
    );

    for (const client of restrictedClients) {
      const members = [...adminMembers, ...perMemberAccess.filter(r => r.client_guids.has(client.guid)).map(r => r.teamMember)];
      result.push({ name: client.display_name, members });
    }
    for (const company of restrictedCompanies) {
      const members = [...adminMembers, ...perMemberAccess.filter(r => r.company_guids.has(company.guid)).map(r => r.teamMember)];
      result.push({ name: company.name, members });
    }
    return result;
  }

  // Flattened, de-duplicated version of the per-entity pool above - used by the "Manage Team Member
  // Skills" modal, where we just want every member who could conceivably end up on this project.
  async ResolveEffectiveTeamMembersForSkillCheck(): Promise<any[]> {
    const perEntity = await this.ResolveTeamMembersPerAssignedEntity();
    const seen = new Set<string>();
    const flattened: any[] = [];
    for (const entity of perEntity) {
      for (const member of entity.members) {
        if (!seen.has(member.guid)) {
          seen.add(member.guid);
          flattened.push(member);
        }
      }
    }
    return flattened;
  }

  // Warns (for now) when, for one or more of the assigned clients/companies, nobody who could work on
  // that specific one has all the skills a task requires. Doesn't apply to templates - there's no
  // concrete set of clients/team members to check against yet.
  async CheckTaskSkillCoverage() {
    this.skills_warning_ignored = false;
    if (this.project.create_template || !this.project.tasks || this.project.tasks.length === 0) {
      this.skills_warning = false;
      this.skills_warning_message = '';
      (this.project.tasks || []).forEach((t: any) => { t.skill_warning = false; t.skill_warning_detail = ''; });
      return;
    }
    this.checking_task_skills = true;
    try {
      const perEntity = await this.ResolveTeamMembersPerAssignedEntity();
      const taskMessages: string[] = [];
      for (const task of this.project.tasks) {
        const requiredSkillGuids: string[] = (task.skills || []).map((s: any) => s.guid);
        // A task given to a specific team member isn't checked - they've said who should work on it, so it
        // doesn't matter whether that person (or anyone else) has the task's skills.
        if (requiredSkillGuids.length === 0 || task.assigned_member_guid) {
          task.skill_warning = false;
          task.skill_warning_detail = '';
          continue;
        }
        const uncoveredNames = perEntity
          .filter(entity => !entity.members.some((tm: any) => {
            const memberSkillGuids = new Set((tm.skills || []).map((s: any) => s.guid));
            return requiredSkillGuids.every(guid => memberSkillGuids.has(guid));
          }))
          .map(entity => entity.name);

        task.skill_warning = uncoveredNames.length > 0;
        task.skill_warning_detail = uncoveredNames.length > 0
          ? `No team member with access to ${uncoveredNames.join(', ')} has the required skills for this task.`
          : '';
        if (uncoveredNames.length > 0) taskMessages.push(`"${task.title}" - missing coverage for: ${uncoveredNames.join(', ')}`);
      }
      this.skills_warning_message = taskMessages.length > 0
        ? `The following tasks are missing skill coverage for some assigned contacts/companies because no team member with access to them has the required skills:\n${taskMessages.join('\n')}`
        : '';
      this.skills_warning = taskMessages.length > 0;
    } finally {
      this.checking_task_skills = false;
    }
  }

  // Lets the user acknowledge the skill conflict so the Next button on this step stops being blocked
  // by it - it doesn't create the project, just clears this one condition from ProjectTasksCheck().
  IgnoreSkillsConflict() {
    this.skills_warning_ignored = true;
  }

  async OpenManageTeamMemberSkillsModal() {
    const candidates = await this.ResolveEffectiveTeamMembersForSkillCheck();

    // All skills the project's tasks require, deduped - the modal derives which of these are still
    // uncovered live, so it stays accurate as skills get assigned within the modal itself.
    const requiredSkillsByGuid = new Map<string, any>();
    for (const task of this.project.tasks) {
      if (task.assigned_member_guid) continue; // given to a specific team member - its skills aren't checked
      for (const skill of (task.skills || [])) {
        requiredSkillsByGuid.set(skill.guid, skill);
      }
    }

    const modal = await this.modalController.create({
      component: ManageSkillsComponent,
      cssClass: 'large-modal secondary-modal',
      backdropDismiss: true,
      componentProps: { team_members: candidates, required_skills: [...requiredSkillsByGuid.values()] }
    });
    modal.onDidDismiss().then(() => {
      this.CheckTaskSkillCoverage();
    });
    return await modal.present();
  }


  async OpenTaskSkillsModal(task: any) {
    if (!task.skills) task.skills = [];
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'team_members',
        selected_tags: task.skills,
        title: 'Select Skills',
        source_header: 'Available Skills',
        target_header: 'Assigned Skills',
        name: 'Skill'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        task.skills = data.data;
        this.CheckTaskSkillCoverage();
      }
    });
    return await modal.present();
  }

  // Edits a task's dates straight from the task list (project dates / relative to the project / fixed)
  async OpenTaskDatesModal(task: any) {
    const modal = await this.modalController.create({
      component: TaskDatesComponent,
      cssClass: 'tall-modal secondary-modal',
      backdropDismiss: false,
      componentProps: { task, project: this.project }
    });
    modal.onDidDismiss().then((data: any) => {
      if (!data?.data) return;
      Object.assign(task, data.data);
      (task.steps || []).forEach((step: any) => AlignStepDueDateToTask(task, step));
    });
    return await modal.present();
  }

  GetAssignmentType(task) {
    if (task.assignment_settings == 0) return "Project Leader Assigns";
    else if (task.assignment_settings == 1) return "Team Members can Claim";
    else if (task.assignment_settings == 2) return this.GetTeamMemberDisplayNameByGuid(task.assigned_member_guid);
    else if (task.assignment_settings == 3) return "Auto Assigned";
  }

  get selectedContactCount(): number {
    return (this.project.assigned_clients?.length || 0) + (this.project.assigned_companies?.length || 0);
  }

  get singleSelectedName(): string {
    if (this.project.assigned_clients?.length > 0) return this.project.assigned_clients[0].display_name;
    if (this.project.assigned_companies?.length > 0) return this.project.assigned_companies[0].name;
    return '';
  }

  get multipleSelectedLabel(): string {
    const hasClients = (this.project.assigned_clients?.length || 0) > 0;
    const hasCompanies = (this.project.assigned_companies?.length || 0) > 0;
    if (hasClients && hasCompanies) return 'Multiple Selected';
    if (hasCompanies) return 'Multiple Companies';
    return 'Multiple Clients';
  }

  GetSelectedClientTooltip(): string {
    const names = [
      ...(this.project.assigned_clients || []).map((c: any) => c.display_name),
      ...(this.project.assigned_companies || []).map((c: any) => c.name),
    ];
    return names.join('\n');
  }

  OnAssignmentSettingsChange() {
    for (const task of (this.project.tasks || [])) this.ApplyTaskAssignment(task);
  }

  // Any task can be given a specific team member, whatever the project's Task Assignment is - that task is
  // then "Specify Team Members" (2) on its own, which the API honours per task (e.g. auto-assign only runs on
  // tasks that are still 3). A task without one follows the project's setting.
  ApplyTaskAssignment(task: any) {
    task.assigned_member_guid = task.assigned_member_guid || '';
    task.assignment_settings = task.assigned_member_guid ? 2 : (this.project.assignment_settings ?? 0);
  }

  GetTaskMemberOptions() {
    return this.task_member_options_cache.Get(this.project.assignment_settings, this.team_members, this.task_assignable_member_guids);
  }

  GetTeamMemberDisplayNameByGuid(guid) {
    const member = this.team_members.find(tm => tm.guid === guid);
    return member ? member.display_name : '';
  }

  ProjectClientAccessCheck() {


    console.log("~~~~~~~~~~~~~~~~ ProjectClientAccessCheck~~~~~~~~~~~~");
    this.client_access_error_message = '';
    this.client_access_error = false;
    let has_inactive_client = false;
    // Who doesn't have portal access - shown in the warning as badges
    this.client_access_inactive_clients = [];
    this.client_access_inactive_companies = [];
    if (this.project.create_template) return;

    // Check if any assigned companies have inactive default clients
    for (const company of this.project.assigned_companies) {
      let inactive_contact: any = null;
      if (company.default_client?.active == 0) {
        has_inactive_client = true;
        inactive_contact = company.default_client;
      }
      if (company.role_guid) {
        // getEffectiveDefaultClient resolves the role-matched contact against the full client
        // list - company.clients itself only ever carries guid/role_guid/is_default_for_role, never
        // active, and can have no match at all for a role with no default contact configured.
        const role_client = this.getEffectiveDefaultClient(company);
        if (role_client && role_client.active == 0) {
          has_inactive_client = true;
          inactive_contact = role_client;
        }
      }
      if (inactive_contact) this.client_access_inactive_companies.push(company);
    }

    // Check if any assigned clients are inactive
    for (const client of this.project.assigned_clients) {
      if (client.active == 0) {
        has_inactive_client = true;
        this.client_access_inactive_clients.push(client);
      }
    }

    if (!has_inactive_client) {
      console.log("Does not have inactive clients, skipping check");
      return;
    }

    //If we dont even have the steps that are active we can return
    if (!this.HasClientActiveSteps()) {
      console.log("Does not have active client steps, skipping check");
      return;
    }

    // Loop through all tasks in the project
    if (!this.project.force_order) {
      // Non-force-order: check each task independently.
      // Within a task, activate_client_portal (type 10) must appear before any client step.
      // Tasks with no client steps at all are fine and skipped.
      for (const task of this.project.tasks) {
        if (!task.steps || task.steps.length === 0) continue;

        const hasClientStep = task.steps.some(s => ClientStepTypes.includes(Number(s.type)));
        if (!hasClientStep) continue;

        const activateIndex = task.steps.findIndex(s => Number(s.type) === 10);
        const firstClientIndex = task.steps.findIndex(s => ClientStepTypes.includes(Number(s.type)));

        if (activateIndex === -1) {
          this.client_access_error_message += `Task "${task.title}" has client actions but no Activate Client Portal step.\n`;
        } else if (firstClientIndex < activateIndex) {
          this.client_access_error_message += `Task "${task.title}" has a client action before the Activate Client Portal step.\n`;
        }
      }
    } else {
      // Force-order: check across all tasks as one flattened sequence.
      // activate_client_portal must appear before any client step in the project.
      let activateGlobalIndex = -1;
      let firstClientGlobalIndex = -1;
      let globalCount = 0;

      outer:
      for (const task of this.project.tasks) {
        if (!task.steps || task.steps.length === 0) continue;
        for (const step of task.steps) {
          if (activateGlobalIndex === -1 && Number(step.type) === 10) {
            activateGlobalIndex = globalCount;
          }
          if (firstClientGlobalIndex === -1 && ClientStepTypes.includes(Number(step.type))) {
            firstClientGlobalIndex = globalCount;
          }
          if (activateGlobalIndex !== -1 && firstClientGlobalIndex !== -1) break outer;
          globalCount++;
        }
      }

      const hasAnyClientStep = firstClientGlobalIndex !== -1;
      if (hasAnyClientStep) {
        if (activateGlobalIndex === -1) {
          this.client_access_error_message += `This project has client actions but no Activate Client Portal step.\n`;
        } else if (firstClientGlobalIndex < activateGlobalIndex) {
          this.client_access_error_message += `This project has a client action before the Activate Client Portal step.\n`;
        }
      }
    }

    if (this.client_access_error_message == '') this.client_access_error = false;
    else {
      const task_details = this.client_access_error_message;
      this.client_access_error_message =
        'The project can still be created, but any automatic emails will changed to manual, optional emails during the time while these clients do not have portal access.  As soon as you give these client portal access in the future, the automatic emails will operate as normal.\n\nAs an option, you can add our \'Activate Client Portal\' action item to this project so that any clients who don\'t have portal access when this project starts will automatically be invited in as part of this project\'s sequence of events' +
        task_details.trim();
      this.client_access_error = true;
    }

  }


}
